import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
// II0-E5 mitigation note: xlsx has known ReDoS/prototype-pollution CVEs with no
// npm-published fix (SheetJS ships patched builds only from cdn.sheetjs.com, off
// the npm registry). Parsing here runs client-side in the browser on a
// user-uploaded file (see XLSX.read below) — worst case is a DoS of that user's
// own tab, not a server or cross-user issue, so this is accepted for now rather
// than pulling a dependency from an unpinned external CDN. Revisit if a
// registry-published fix lands, or migrate to exceljs/CDN build if this becomes
// a multi-tenant risk.
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KPIDataModal, { ColumnDef } from '../components/fleetDataHub/KPIDataModal';
import { type CustomsLineItem } from '../data/customsAgentSamples';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import '../components/customsAgent/CustomsAgent.css';

// Ensure browser PDF parsing works in Vite builds (without relying on backend fallback).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

type UkOption = {
  code_10digit: string;
  description: string;
  formatted_description?: string;
  number_indents?: number;
  validity_start_date?: string | null;
  validity_end_date?: string | null;
};

type UkOptionsResponse =
  | { ok: true; code8: string; heading: string; count: number; options: UkOption[] }
  | { ok: false; error: string };

type UkDutyResponse =
  | { ok: true; code10: string; description: string; duty_pct: number; vat_pct: number; commodity_url: string }
  | { ok: false; error: string };

type PythonPdfExtractResponse =
  | { ok: true; matrix: string[][]; source?: string }
  | { ok: false; error: string; details?: string };

type PythonPdfHealthResponse = {
  ok?: boolean;
  error?: string;
  details?: string;
};

type ConversionResultRow = {
  hs8: string;
  options_found: number;
  top_code10: string;
  top_description: string;
  status: 'Ready' | 'Needs attention';
  /** Best / runner-up invoice↔tariff match scores at conversion (for ambiguity). */
  desc_best_score?: number;
  desc_second_score?: number;
  /** Count of UK 10-digit candidates after …00 filter (same set used for ranking). */
  candidate_count?: number;
};

/** Manual batch input: EU 8-digit codes only (description/qty/value come from CSV when uploaded). */
type ManualLineItem = {
  eu_cn_8digit: string;
};

const UK_TRADE_TARIFF_SOURCE_LINE = 'Source: UK Trade Tariff (trade-tariff.service.gov.uk).';
const UK_TRADE_TARIFF_SOURCE = `${UK_TRADE_TARIFF_SOURCE_LINE} UK 10-digit codes, descriptions, duty and VAT come from this official HMRC service.`;

/** Shown in the Review options modal when the UK Trade Tariff returns no 10-digit lines for this 8-digit heading. */
const UK_OPTIONS_NO_MATCH_MESSAGE =
  'The 8-digit code has no matching results.\nPlease enter a valid 8-digit HS code according to the UK Trade Tariff website.';

function cleanTariffDescription(input?: string) {
  if (!input) return '';
  return input
    .replace(/<br\s*\/?>/gi, ' - ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;not;/gi, '')
    .replace(/&not;/gi, '')
    .replace(/&amp;/gi, '&')
    .replace(/\u2014/g, '-')
    .replace(/\u00AC/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Standalone “Other” tariff row → explicit fallback label (whole word only). */
function labelOtherFallback(text: string) {
  return text.replace(/\bOther\b/gi, 'Other (fallback)');
}

/** Skip generic tariff / connector words when building n-grams from the invoice (not matched alone). */
const DESCRIPTION_STOP_WORDS = new Set(
  [
    'with', 'of', 'and', 'or', 'the', 'a', 'an', 'for', 'to', 'in', 'on', 'at', 'by', 'from', 'into', 'as', 'is', 'are',
    'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'not', 'no', 'nor', 'but', 'if', 'than', 'then',
    'when', 'where', 'which', 'that', 'this', 'these', 'those', 'there', 'here', 'such', 'same', 'some', 'any', 'all',
    'each', 'every', 'both', 'few', 'more', 'most', 'less', 'other', 'only', 'just', 'also', 'very', 'can', 'will',
    'should', 'could', 'would', 'may', 'might', 'must', 'shall', 'use', 'used', 'using', 'having', 'including',
    'excluding', 'without', 'within', 'between', 'among', 'over', 'under', 'above', 'below', 'up', 'down', 'out', 'off',
    'not', 'more', 'than', 'less',
  ].map((w) => w.toLowerCase()),
);

const MAX_DESCRIPTION_HIGHLIGHTS = 5;
const MIN_INVOICE_NGRAM_LEN = 3;
/** Quick HS lookup list: light keyword marks — prefer words that distinguish one option from siblings. */
const MAX_QUICK_LOOKUP_HIGHLIGHTS = 3;
const MIN_QUICK_LOOKUP_WORD_LEN = 4;
const QUICK_LOOKUP_SKIP_WORDS = new Set(['fallback']);

type DescSpan = { start: number; end: number };

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Map invoice wording to tariff text (hyphen vs space, optional space before units). */
/**
 * Map invoice tokens onto tariff text. Tariff often inserts extra words (“for a”) or hyphens
 * (“single-stage”); only joining with [-\\s]+ misses those — use \\b per word + a short permissive gap.
 */
function flexMatchPhraseInTariff(fullText: string, phrase: string): DescSpan | null {
  const trimmed = phrase.trim();
  if (trimmed.length < MIN_INVOICE_NGRAM_LEN) return null;
  const rawParts = trimmed.split(/\s+/).filter(Boolean);
  if (rawParts.length === 0) return null;

  const body = rawParts
    .map((p) => {
      const e = escapeRegex(p);
      if (/^\d/.test(p)) return e;
      if (p.length <= 1) return `\\b${e}\\b`;
      const base = `\\b${e}\\b`;
      if (p.length >= 4 && !p.endsWith('s')) return `(?:${base}|\\b${escapeRegex(p)}s\\b)`;
      return base;
    })
    .join('[\\s\\S]{0,240}?');

  try {
    const re = new RegExp(body, 'i');
    const m = re.exec(fullText);
    if (!m) return null;
    return { start: m.index, end: m.index + m[0].length };
  } catch {
    return null;
  }
}

function tokenizeInvoiceForNgrams(invoiceCleanLower: string): string[] {
  const s = invoiceCleanLower.replace(/-/g, ' ');
  const raw = s.split(/[^a-z0-9]+/).filter(Boolean);
  const units = new Set(['mm', 'cm', 'm', 'km', 'kg', 'g', 'ml', 'l', 'kw', 'kv', 'v', 'dc', 'vac', 'bar', 'psi', 'c']);
  const out: string[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const t = raw[i];
    const next = raw[i + 1];
    if (next && /^\d/.test(t) && units.has(next)) {
      out.push(`${t}${next}`);
      i += 1;
    } else {
      out.push(t);
    }
  }
  return out;
}

function buildInvoiceNgrams(invoiceLine: string): string[] {
  const inv = cleanTariffDescription(invoiceLine).toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
  if (inv.length < 2) return [];
  const tokens = tokenizeInvoiceForNgrams(inv);
  if (tokens.length === 0) return [];
  const ngrams = new Set<string>();
  const maxN = Math.min(10, tokens.length);
  for (let n = maxN; n >= 1; n -= 1) {
    for (let i = 0; i <= tokens.length - n; i += 1) {
      const slice = tokens.slice(i, i + n);
      if (slice.every((t) => DESCRIPTION_STOP_WORDS.has(t))) continue;
      const phrase = slice.join(' ');
      if (phrase.length < MIN_INVOICE_NGRAM_LEN) continue;
      if (n === 1) {
        const w = slice[0];
        if (DESCRIPTION_STOP_WORDS.has(w)) continue;
        if (/^\d+$/.test(w)) continue;
      }
      ngrams.add(phrase);
    }
  }
  return [...ngrams].sort((a, b) => b.length - a.length);
}

/**
 * Non-overlapping spans (capped): invoice tokens/phrases matched into tariff with flexible gaps.
 * Measurements only if present on the invoice; no tariff-only dictionary.
 */
function findInvoiceVerbatimSpans(finalTariffText: string, invoiceLine?: string): DescSpan[] {
  if (!invoiceLine?.trim()) return [];
  const text = finalTariffText;
  const ngrams = buildInvoiceNgrams(invoiceLine);
  const chosen: DescSpan[] = [];

  for (const phrase of ngrams) {
    if (chosen.length >= MAX_DESCRIPTION_HIGHLIGHTS) break;
    const match = flexMatchPhraseInTariff(text, phrase);
    if (!match) continue;
    const overlaps = chosen.some((c) => !(match.end <= c.start || match.start >= c.end));
    if (overlaps) continue;
    chosen.push(match);
  }

  return chosen.sort((a, b) => a.start - b.start || a.end - b.end);
}

/**
 * Highlight a few discriminative tokens in this option vs sibling descriptions (document frequency).
 * Caps matches and skips stop words so the Quick HS list stays scannable, not noisy.
 */
function findQuickLookupDiscriminativeSpans(displayText: string, allDescriptionsLower: string[]): DescSpan[] {
  if (!displayText.trim() || allDescriptionsLower.length === 0) return [];

  const wordDocFreq = new Map<string, number>();
  for (const d of allDescriptionsLower) {
    const wordsInDoc = new Set(
      d
        .split(/[^a-z0-9]+/)
        .filter(
          (w) =>
            w.length >= MIN_QUICK_LOOKUP_WORD_LEN &&
            !DESCRIPTION_STOP_WORDS.has(w) &&
            !QUICK_LOOKUP_SKIP_WORDS.has(w) &&
            !/^\d+$/.test(w),
        ),
    );
    for (const w of wordsInDoc) {
      wordDocFreq.set(w, (wordDocFreq.get(w) || 0) + 1);
    }
  }

  type Cand = { start: number; end: number; docFreq: number; len: number };
  const cands: Cand[] = [];
  const seenWord = new Set<string>();
  const tokenRe = /[A-Za-z0-9]+(?:[-'][A-Za-z0-9]+)*/g;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(displayText)) !== null) {
    const raw = m[0];
    const w = raw.toLowerCase();
    if (raw.length < MIN_QUICK_LOOKUP_WORD_LEN) continue;
    if (DESCRIPTION_STOP_WORDS.has(w) || QUICK_LOOKUP_SKIP_WORDS.has(w) || /^\d+$/.test(w)) continue;
    if (seenWord.has(w)) continue;
    seenWord.add(w);
    const docFreq = wordDocFreq.get(w);
    if (docFreq == null || docFreq < 1) continue;
    cands.push({ start: m.index, end: m.index + raw.length, docFreq, len: raw.length });
  }

  cands.sort((a, b) => (a.docFreq - b.docFreq) || (b.len - a.len) || (a.start - b.start));

  const chosen: DescSpan[] = [];
  for (const c of cands) {
    if (chosen.length >= MAX_QUICK_LOOKUP_HIGHLIGHTS) break;
    const overlaps = chosen.some((ch) => !(c.end <= ch.start || c.start >= ch.end));
    if (overlaps) continue;
    chosen.push({ start: c.start, end: c.end });
  }
  return chosen.sort((a, b) => a.start - b.start || a.end - b.end);
}

function renderQuickLookupOptionDescription(raw: string | undefined, allOptions: UkOption[]): ReactNode {
  const cleaned = cleanTariffDescription(raw);
  if (!cleaned) return '—';
  const text = labelOtherFallback(cleaned);
  const allLower = allOptions.map((o) => cleanTariffDescription(o.description || '').toLowerCase());
  const spans = findQuickLookupDiscriminativeSpans(text, allLower);
  if (spans.length === 0) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;
  spans.forEach((s, i) => {
    if (s.start > cursor) parts.push(text.slice(cursor, s.start));
    parts.push(
      <mark key={`qk-${s.start}-${s.end}-${i}`} className="ca-desc-highlight ca-desc-highlight--quick">
        {text.slice(s.start, s.end)}
      </mark>,
    );
    cursor = s.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span className="ca-desc-text">{parts}</span>;
}

/** Amber highlights — Options / Review modals only; driven by invoice line wording (capped). */
function renderHighlightedTariffDescription(raw?: string, invoiceLineDescription?: string): ReactNode {
  const cleaned = cleanTariffDescription(raw);
  if (!cleaned) return '—';
  const text = labelOtherFallback(cleaned);
  const spans = findInvoiceVerbatimSpans(text, invoiceLineDescription);
  if (spans.length === 0) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;
  spans.forEach((s, i) => {
    if (s.start > cursor) parts.push(text.slice(cursor, s.start));
    parts.push(
      <mark key={`h-${s.start}-${s.end}-${i}`} className="ca-desc-highlight">
        {text.slice(s.start, s.end)}
      </mark>,
    );
    cursor = s.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span className="ca-desc-text">{parts}</span>;
}

/** Plain description for invoice / main grid: clean HTML + “Other (fallback)”, no keyword marks. */
function formatTariffDescriptionPlain(input?: string): string {
  const c = cleanTariffDescription(input);
  if (!c) return '—';
  return labelOtherFallback(c);
}

function optionType(code10?: string, indent?: number) {
  const c = String(code10 || '');
  const n = Number(indent ?? 0);
  if (c.endsWith('00') || n <= 1) return 'General';
  return 'Specific';
}

/** 10-digit code: 4+4+2 groups, single muted colour (modal tables). */
function renderUkCommodityCode10(value: string) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) {
    return <span className="uk-commodity-code-fallback">{value || '—'}</span>;
  }
  const a = digits.slice(0, 4);
  const b = digits.slice(4, 8);
  const c = digits.slice(8, 10);
  return (
    <span className="uk-commodity-code-segments" aria-label={digits}>
      <span className="uk-commodity-code-base">
        <span className="uk-commodity-code-base-part">{a}</span>
        <span className="uk-commodity-code-base-part">{b}</span>
        <span className="uk-commodity-code-base-part uk-commodity-code-base-part--suffix">{c}</span>
      </span>
    </span>
  );
}

/** Review table + Quick HS lookup: last 2 digits emerald bold (8+2 split). */
function renderUkCode10EmeraldSuffix(code10: string) {
  const digits = String(code10 || '').replace(/\D/g, '');
  if (digits.length !== 10) return <span className="mono-code">{code10}</span>;
  return (
    <span className="mono-code mono-code--uk-ok" title={digits}>
      <span>{digits.slice(0, 8)}</span>
      <span className="mono-code-suffix-emerald">{digits.slice(8)}</span>
    </span>
  );
}

function formatTariffDate(value: unknown): string {
  if (value == null || value === '') return '—';
  const s = String(value).trim();
  if (s === '-' || s === '–') return '—';
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
  return s;
}

function tariffYearOnly(value: unknown): string | null {
  const d = formatTariffDate(value);
  if (d === '—') return null;
  const y = d.slice(0, 4);
  return /^\d{4}$/.test(y) ? y : null;
}

function validityEndIsOpen(value: unknown): boolean {
  if (value == null) return true;
  const s = String(value).trim();
  if (s === '' || s === '-' || s === '–') return true;
  return formatTariffDate(value) === '—';
}

/** One column: year from valid-from; if valid-to is set (not “-”), show start–end years. */
function renderValidSince(_: unknown, row: UkOption) {
  const startY = tariffYearOnly(row?.validity_start_date);
  if (!startY) return '—';
  if (validityEndIsOpen(row?.validity_end_date)) return startY;
  const endY = tariffYearOnly(row?.validity_end_date);
  if (!endY) return startY;
  if (endY === startY) return startY;
  return `${startY}–${endY}`;
}

function buildTariffOptionsCheckboxColumns(
  invoiceLineDescription: string | undefined,
  selectedCode10: string | null,
  hs8: string,
  onSelectCode10: (code10: string | null) => void,
): ColumnDef<UkOption>[] {
  return [
    { key: 'code_10digit', header: 'UK 10-digit', render: (v) => renderUkCommodityCode10(String(v ?? '')) },
    {
      key: 'description',
      header: 'Description',
      render: (v) => renderHighlightedTariffDescription(String(v || ''), invoiceLineDescription),
    },
    { key: 'code_10digit', header: 'Type', align: 'right', render: (_, row) => optionType(row?.code_10digit, row?.number_indents) },
    { key: 'validity_start_date', header: 'Valid since', render: (v, row) => renderValidSince(v, row as UkOption) },
    {
      key: 'code_10digit',
      header: 'Select',
      align: 'right',
      render: (_, row) => {
        const opt = row as UkOption;
        const code = String(opt.code_10digit ?? '');
        const inputId = `uk-opt-select-${hs8}-${code}`;
        return (
          <input
            id={inputId}
            type="checkbox"
            className="h-4 w-4 rounded border-accent bg-card text-primary focus:ring-primary focus:ring-offset-0 focus:ring-offset-background cursor-pointer"
            checked={selectedCode10 != null && selectedCode10 === code}
            onChange={(e) => {
              onSelectCode10(e.target.checked ? code : null);
            }}
            title={`Select UK 10-digit ${code} for HS ${hs8} · use Apply selection to confirm`}
            aria-label={`Select UK commodity ${code} for HS ${hs8}`}
          />
        );
      },
    },
  ];
}

const invoiceColumns: ColumnDef[] = [
  { key: 'page', header: 'Page', align: 'right' },
  { key: 'item_code', header: 'Item' },
  { key: 'description', header: 'Description', render: (v) => formatTariffDescriptionPlain(String(v || '')) },
  { key: 'eu_cn_8digit', header: 'HS (8-digit)' },
  { key: 'qty', header: 'Qty', align: 'right' },
  { key: 'unit', header: 'Unit' },
  { key: 'net_eur', header: 'Net (€)', align: 'right', render: (v) => (v != null ? `€${Number(v).toFixed(2)}` : '-') },
];

function isValid8(code: string) {
  return /^\d{8}$/.test(code.trim());
}

/** Real EU/HS headings use chapters 01–97; rejects order refs like 0000054321 → 00000543 (chapter 00). */
function isPlausibleEuCommodityHs8(code8: string): boolean {
  const c = code8.trim();
  if (!/^\d{8}$/.test(c)) return false;
  if (c === '00000000') return false;
  const chapter = Number(c.slice(0, 2));
  return chapter >= 1 && chapter <= 97;
}

function adjustHs8ToPlausible(raw: string): string {
  const t = raw.trim();
  return isPlausibleEuCommodityHs8(t) ? t : '';
}

/** Like toHs8 but never returns pseudo-codes from long numeric IDs. */
function toCommodityHs8(value: string): string {
  return adjustHs8ToPlausible(toHs8(value));
}

/** Checkbox state for UK options modal: explicit user choice overrides conversion row default. */
function getUkOptionSelectionForHs8(
  hs8: string,
  overrides: Record<string, string | null>,
  conversionRows: ConversionResultRow[] | null,
): string | null {
  if (Object.prototype.hasOwnProperty.call(overrides, hs8)) {
    return overrides[hs8];
  }
  const conv = conversionRows?.find((r) => r.hs8 === hs8);
  const t = conv?.top_code10;
  return t && t !== '-' ? t : null;
}

const CONF_DESC_CAP = 72;

/** Single displayed % for all green (high-band) rows — avoids implying false precision between lines. */
const CONFIDENCE_HI_DISPLAY_PCT = 98;

/**
 * Review-table confidence (36–98%): combines Ready state, invoice↔chosen-tariff text match,
 * separation from the next-best option, and sibling count — not a statistical CI. (Duty/VAT are shown separately and do not affect this score.)
 * High band (green) always shows the same % (see CONFIDENCE_HI_DISPLAY_PCT) for now.
 */
function computeUkLineConfidence(args: {
  hs8: string;
  lineDescription: string | undefined;
  conv: ConversionResultRow | undefined;
  status: 'Ready' | 'Needs attention';
}): { pct: number; tier: 'hi' | 'med' | 'lo' } {
  const { hs8, lineDescription, conv, status } = args;
  const invalidHs = !isValid8(hs8);
  const optionsFound = conv?.options_found ?? 0;
  const hasOptions = Boolean(conv && optionsFound > 0);

  if (invalidHs || !hasOptions) {
    const pct = invalidHs ? 25 : 35;
    return { pct, tier: 'lo' };
  }

  const pseudo: UkOption = {
    code_10digit: conv!.top_code10,
    description: conv!.top_description,
    formatted_description: undefined,
    number_indents: undefined,
    validity_start_date: undefined,
    validity_end_date: undefined,
  };
  const lineScore = lineDescription?.trim()
    ? scoreUkOptionForInvoice(lineDescription, pseudo)
    : (conv!.desc_best_score ?? 0);
  const bestMeta = Math.max(conv!.desc_best_score ?? 0, lineScore, 1e-6);
  const secondMeta = conv!.desc_second_score ?? 0;
  const nCand = Math.max(1, conv!.candidate_count ?? optionsFound);

  const matchNorm = Math.min(1, lineScore / CONF_DESC_CAP);
  const separation =
    nCand <= 1
      ? 1
      : secondMeta <= 0
        ? 1
        : Math.min(1, Math.max(0, (bestMeta - secondMeta) / (bestMeta + 1e-6)));

  const ambiguityDeduction = nCand <= 1 ? 0 : Math.min(16, Math.round((nCand - 1) * 2.5));

  let points = 42;
  if (status === 'Ready') points += 36;
  points += Math.round(20 * matchNorm);
  points += Math.round(14 * separation);
  points -= ambiguityDeduction;

  const rawPct = Math.max(36, Math.min(98, points));
  const tier: 'hi' | 'med' | 'lo' = rawPct >= 85 ? 'hi' : rawPct >= 68 ? 'med' : 'lo';
  const pct = tier === 'hi' ? CONFIDENCE_HI_DISPLAY_PCT : rawPct;
  return { pct, tier };
}

function normalizeHeader(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function splitDelimitedLine(line: string, delimiter: string) {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function detectDelimiter(lines: string[]) {
  const candidates = [',', ';', '\t', '|'];
  let best = ',';
  let bestScore = -1;
  const sample = lines.slice(0, 5);

  for (const d of candidates) {
    const score = sample.reduce((sum, line) => sum + splitDelimitedLine(line, d).length, 0);
    if (score > bestScore) {
      best = d;
      bestScore = score;
    }
  }
  return best;
}

function toNumber(value: string, fallback = 0) {
  const n = Number(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

function toHs8(value: string) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : '';
}

/**
 * Find an 8-digit EU/CN-style commodity code inside free text.
 * Uses invoice phrases first, then digit-separated tokens (avoids "1/2 inch" + code → wrong HS).
 */
function extractHs8FromText(text: string): string {
  const raw = String(text || '');
  if (!raw.trim()) return '';
  const hasCustomsContext =
    /combined\s+nomenclature|nomenclature|taric|hs\s*code|commodity|customs|cn\s*code|kn\s*code/i.test(raw);
  const hasMetaContext =
    /\bdate\b|\byour order\b|reg\.?\s*(?:no|number)\b|company\s*(?:reg|number)|vat\s*(?:no|number)\b/i.test(raw);
  const hasDatePattern = /\b20\d{2}[./-]\d{2}[./-]\d{2}\b/.test(raw);
  if ((hasMetaContext || hasDatePattern) && !hasCustomsContext) return '';

  const phrasePatterns: RegExp[] = [
    /COMBINED\s+NOMENCLATURE\s*[:\s]+\*?\s*(\d{8})\b/gi,
    /NOMENCLATURE\s*(?:CODE)?\s*[:\s]+\*?\s*(\d{8})\b/gi,
    /TARIC\s*[:\s#]?\s*(\d{8})\b/gi,
    /(?:HS|H\.S\.|CN|C\.N\.)\s*(?:CODE|NO\.?|NUMBER|NR\.?)?\s*[:\s#]?\s*(\d{8})\b/gi,
    /COMMODITY\s*(?:CODE|NUMBER)?\s*[:\s#]?\s*(\d{8})\b/gi,
    /CUSTOMS\s*(?:CODE|HS)?\s*[:\s#]?\s*(\d{8})\b/gi,
    /ZOLL\s*(?:TARIF|NUMMER|POSITION)?\s*[:\s#]?\s*(\d{8})\b/gi,
    /KN\s*CODE\s*[:\s#]?\s*(\d{8})\b/gi,
    /\b(\d{8})\s*[*†]?\s*\(?\s*[A-Z]{2}\s*\)?\b/g,
    /\b(\d{8})\s*\/\s*\d{2}\b/g,
  ];

  for (const re of phrasePatterns) {
    const m = raw.match(re);
    if (m?.[1] && isPlausibleEuCommodityHs8(m[1])) return m[1].trim();
  }

  const tokens = raw.split(/\D+/).filter(Boolean);
  const eightDigit: string[] = [];
  for (const t of tokens) {
    if (isPlausibleEuCommodityHs8(t)) eightDigit.push(t);
    else if (/^\d{10}$/.test(t)) {
      const head = t.slice(0, 8);
      if (isPlausibleEuCommodityHs8(head)) eightDigit.push(head);
    }
  }
  if (eightDigit.length === 1) return eightDigit[0];
  if (eightDigit.length > 1) return eightDigit[eightDigit.length - 1];

  return '';
}

/** Resolve HS8 from dedicated column, description, then any other cell (codes are often embedded in text). */
function resolveHs8ForTableRow(
  cols: string[],
  hsIdx: number,
  descIdx: number,
  description: string,
): string {
  const hsCell = hsIdx >= 0 ? cols[hsIdx] || '' : '';
  let hs8 = toCommodityHs8(hsCell);
  if (!hs8 && hsCell.trim()) hs8 = extractHs8FromText(hsCell);
  if (!hs8 && description.trim()) hs8 = extractHs8FromText(description);
  if (!hs8) {
    for (let ci = 0; ci < cols.length; ci += 1) {
      if (ci === hsIdx || ci === descIdx) continue;
      const cell = cols[ci];
      if (!cell || cell.length < 6) continue;
      hs8 = extractHs8FromText(cell);
      if (hs8) break;
    }
  }
  if (!hs8) {
    // Do not strip all digits from arbitrary non-HS cells: it turns metadata like
    // "2026.02.15" or "Reg. No.14405265" into false 8-digit HS codes.
  }
  return hs8;
}

/** Product / customs language — if present, the line is probably a real item even without value in parse heuristics. */
const INVOICE_LINE_PRODUCT_HINT =
  /combined\s+nomenclature|nomenclature\s*:\s*\d|taric\b|hs\s*code|commodity|hydraulic|rubber|filter|cartridge|per\s+metre|per\s+meter|micron|hose|element|seal|gasket|valve|pump|cable|steel|plastic|aluminium|aluminum|brass|copper|iron|bolt|nut|washer|bearing|motor|sensor|switch|wire|pipe|fitting|coupling|adapter|tool|machine|part|assembly|kit\b|spare|component|goods|material/i;

/**
 * Skip pasted invoice banner rows (dates, “Your order: …”) that are not commodity lines.
 * Requires no plausible HS, no qty/net, and no product/customs wording.
 */
function looksLikeInvoiceMetadataRow(description: string, hs8: string, qty: number, net: number): boolean {
  const d = description.trim();
  if (!d) return false;
  if (qty > 0 || net > 0) return false;
  if (isPlausibleEuCommodityHs8(hs8)) return false;
  if (INVOICE_LINE_PRODUCT_HINT.test(d)) return false;

  return (
    /\byour order\s*:/i.test(d) ||
    /^\s*date\s*:/im.test(d) ||
    /\binvoice\s*(no|number|#)\s*:/i.test(d) ||
    /\bpage\s+\d+\s*(?:of|\/)\s*\d+\b/i.test(d) ||
    /^[\s,:./-]*date\s*:\s*\d{4}[./-]\d{2}[./-]\d{2}/i.test(d)
  );
}

/**
 * Tail rows / range noise: empty description but a stray number in another cell (SKU, row id)
 * can be misread as HS8. Not a real invoice line without any line text or commercial amounts.
 */
function isStraySpreadsheetRow(description: string, qty: number, net: number): boolean {
  return !description.trim() && qty === 0 && net === 0;
}

/** Normalize one spreadsheet/CSV cell to a string (SheetJS may return number or Date). */
function cellString(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v.replace(/\uFEFF/g, '').trim();
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (Number.isInteger(v) && Math.abs(v) >= 1e9) return v.toFixed(0);
    return String(v);
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v).replace(/\uFEFF/g, '').trim();
}

type InvoiceTableSource = 'csv_upload' | 'xlsx_upload' | 'pdf_upload';

function headerColumnIndices(headersNorm: string[]) {
  const idx = (keys: string[]) => headersNorm.findIndex((h) => keys.includes(h));
  return {
    pageIdx: idx(['page', 'pageno', 'pagenumber']),
    itemIdx: idx(['itemcode', 'item', 'sku', 'code', 'itemno']),
    descIdx: idx(['description', 'desc', 'product', 'itemdescription']),
    hsIdx: idx(['eucn8digit', 'hs8', 'hscode', 'hs', 'commoditycode', 'commodity']),
    qtyIdx: idx(['qty', 'quantity']),
    unitIdx: idx(['unit', 'uom']),
    unitPriceIdx: idx([
      'unitpriceeur',
      'unitprice',
      'uprice',
      'price',
      'rate',
      'priceperunit',
      'netunitprice',
    ]),
    netIdx: idx([
      'neteur',
      'net',
      'amount',
      'linetotal',
      'linevalue',
      'netamount',
      'lineamount',
      'totalline',
      'value',
    ]),
  };
}

function rowLooksLikeInvoiceHeader(headersNorm: string[]) {
  const { descIdx, hsIdx, itemIdx, qtyIdx, netIdx } = headerColumnIndices(headersNorm);
  return [descIdx, hsIdx, itemIdx, qtyIdx, netIdx].some((i) => i >= 0);
}

function parseDataRowsWithHeaders(
  matrix: string[][],
  headerRowIdx: number,
  source: InvoiceTableSource,
): CustomsLineItem[] {
  const headerRow = matrix[headerRowIdx] || [];
  const headersNorm = headerRow.map(normalizeHeader);
  const { pageIdx, itemIdx, descIdx, hsIdx, qtyIdx, unitIdx, unitPriceIdx, netIdx } =
    headerColumnIndices(headersNorm);

  return matrix
    .slice(headerRowIdx + 1)
    .map((cols, i) => {
      const description = (descIdx >= 0 ? cols[descIdx] : '') || '';
      const hs8 = resolveHs8ForTableRow(cols, hsIdx, descIdx, description);
      const qty = toNumber(qtyIdx >= 0 ? cols[qtyIdx] : '', 0);
      const net = toNumber(netIdx >= 0 ? cols[netIdx] : '', 0);
      if (looksLikeInvoiceMetadataRow(description, hs8, qty, net)) return null;
      if (!description && !hs8) return null;
      return {
        page: toNumber(pageIdx >= 0 ? cols[pageIdx] : '', 1),
        item_code: (itemIdx >= 0 ? cols[itemIdx] : '') || `ROW-${i + 1}`,
        description,
        eu_cn_8digit: hs8,
        qty,
        unit: (unitIdx >= 0 ? cols[unitIdx] : '') || 'PC',
        unit_price_eur: toNumber(unitPriceIdx >= 0 ? cols[unitPriceIdx] : '', 0),
        net_eur: net,
        source,
      } as CustomsLineItem;
    })
    .filter((row): row is CustomsLineItem => Boolean(row));
}

/** Scan top rows for a header line (spreadsheets often have title rows above the table). */
function findInvoiceHeaderRowIndex(matrix: string[][], maxScan = 60): number {
  const limit = Math.min(matrix.length, maxScan);
  for (let r = 0; r < limit; r += 1) {
    const headersNorm = (matrix[r] || []).map(normalizeHeader);
    if (!headersNorm.some((h) => h.length > 0)) continue;
    if (rowLooksLikeInvoiceHeader(headersNorm)) return r;
  }
  return -1;
}

function parseRowsToLineItems(matrix: string[][], source: InvoiceTableSource): CustomsLineItem[] {
  const cleaned = matrix.map((row) => row.map((c) => String(c ?? '').replace(/\uFEFF/g, '').trim()));
  const headerIdx = findInvoiceHeaderRowIndex(cleaned);
  if (headerIdx >= 0) return parseDataRowsWithHeaders(cleaned, headerIdx, source);
  return cleaned
    .map((cols, i) => parseRowHeuristics(cols, i, source))
    .filter((row): row is CustomsLineItem => Boolean(row));
}

function parseRowHeuristics(cols: string[], rowIndex: number, source: InvoiceTableSource): CustomsLineItem | null {
  const textCandidates = cols.filter((c) => /[a-z]/i.test(c) && !/^\d+([.,]\d+)?$/.test(c));
  const description = (textCandidates.sort((a, b) => b.length - a.length)[0] || '').trim();
  let hs8 = extractHs8FromText(description);
  if (!hs8) hs8 = extractHs8FromText(cols.join('\n'));
  if (!hs8) {
    for (const c of cols) {
      hs8 = extractHs8FromText(c);
      if (hs8) break;
    }
  }
  if (!hs8) {
    // Avoid blind digit-stripping fallback on headerless PDF rows; this captures
    // date / company registration numbers as fake HS codes.
  }
  if (!description && !hs8) return null;

  const numericCandidates = cols
    .map((c) => ({ raw: c, value: toNumber(c, Number.NaN) }))
    .filter((v) => Number.isFinite(v.value));

  const qty = numericCandidates[0]?.value ?? 0;
  const unitPrice = numericCandidates[1]?.value ?? 0;
  const net = numericCandidates[2]?.value ?? 0;

  if (isStraySpreadsheetRow(description, qty, net)) return null;
  if (looksLikeInvoiceMetadataRow(description, hs8, qty, net)) return null;

  return {
    page: 1,
    item_code: cols[0] || `ROW-${rowIndex + 1}`,
    description,
    eu_cn_8digit: hs8,
    qty,
    unit: 'PC',
    unit_price_eur: unitPrice,
    net_eur: net,
    source,
  };
}

function parseCsvToLineItems(csvText: string): CustomsLineItem[] {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.replace(/\uFEFF/g, '').trim())
    .filter(Boolean);
  if (lines.length < 1) return [];

  const delimiter = detectDelimiter(lines);
  const matrix = lines.map((line) => splitDelimitedLine(line, delimiter));
  return parseRowsToLineItems(matrix, 'csv_upload');
}

/** Pick the sheet that yields the most line items (workbooks often have cover/summary tabs). */
function parseXlsxArrayBuffer(buf: ArrayBuffer): CustomsLineItem[] {
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  let best: CustomsLineItem[] = [];
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      defval: '',
      blankrows: false,
    }) as unknown[][];
    const matrix: string[][] = (raw || []).map((row) =>
      (Array.isArray(row) ? row : []).map((cell) => cellString(cell)),
    );
    const items = parseRowsToLineItems(matrix, 'xlsx_upload');
    if (items.length > best.length) best = items;
  }
  return best;
}

type PdfTextItemLike = {
  str?: unknown;
  transform?: unknown;
  width?: unknown;
  hasEOL?: unknown;
};

type PdfRowBucket = { y: number; parts: { x: number; text: string }[] };

function groupPdfItemsByRow(items: PdfTextItemLike[]): PdfRowBucket[] {
  // Group glyph chunks into visual rows using Y coordinate.
  const rows: PdfRowBucket[] = [];
  const Y_TOLERANCE = 2;

  for (const rawItem of items) {
    const text = typeof rawItem?.str === 'string' ? rawItem.str.trim() : '';
    if (!text) continue;
    const tf = Array.isArray(rawItem?.transform) ? rawItem.transform : null;
    if (!tf || tf.length < 6) continue;
    const x = Number(tf[4]);
    const y = Number(tf[5]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    let row = rows.find((r) => Math.abs(r.y - y) <= Y_TOLERANCE);
    if (!row) {
      row = { y, parts: [] };
      rows.push(row);
    }
    row.parts.push({ x, text });
  }
  rows.sort((a, b) => b.y - a.y);
  return rows;
}

function extractPdfTextLines(items: PdfTextItemLike[]): string[] {
  const rows = groupPdfItemsByRow(items);
  return rows
    .map((row) =>
      row.parts
        .sort((a, b) => a.x - b.x)
        .map((p) => p.text)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean);
}

function extractPdfTextLinesByStream(items: PdfTextItemLike[]): string[] {
  const lines: string[] = [];
  let current = '';
  for (const rawItem of items) {
    const text = typeof rawItem?.str === 'string' ? rawItem.str : '';
    if (!text) continue;
    const trimmed = text.replace(/\s+/g, ' ').trim();
    if (!trimmed) continue;
    current = current ? `${current} ${trimmed}` : trimmed;
    if (rawItem?.hasEOL === true) {
      lines.push(current.replace(/\s+/g, ' ').trim());
      current = '';
    }
  }
  if (current) lines.push(current.replace(/\s+/g, ' ').trim());
  return lines.filter(Boolean);
}

function extractPdfMatrixByColumns(items: PdfTextItemLike[]): string[][] {
  const rows = groupPdfItemsByRow(items);
  const out: string[][] = [];
  const X_TOLERANCE = 12;

  for (const row of rows) {
    const sorted = row.parts.sort((a, b) => a.x - b.x);
    const cells: { x: number; text: string }[] = [];
    for (const part of sorted) {
      const last = cells[cells.length - 1];
      if (!last || Math.abs(part.x - last.x) > X_TOLERANCE) {
        cells.push({ x: part.x, text: part.text });
      } else {
        last.text = `${last.text} ${part.text}`.replace(/\s+/g, ' ').trim();
      }
    }
    const rowCells = cells.map((c) => c.text.trim()).filter(Boolean);
    if (rowCells.length > 0) out.push(rowCells);
  }
  return out;
}

function isPdfCurrencyOnlyLine(text: string): boolean {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  return /^£?\s?\d{1,3}(,\d{3})*(\.\d{2})?$/.test(t);
}

/**
 * PDF invoices often place "Combined nomenclature: XXXXXXXX" on a second line under
 * description. Merge those continuation rows into the previous item and drop amount-only
 * artifact rows (totals carried over from page footer/header areas).
 */
function normalizePdfInvoiceMatrix(matrix: string[][]): string[][] {
  const cleaned = matrix.map((row) => row.map((c) => String(c ?? '').replace(/\uFEFF/g, '').trim()));
  const headerIdx = findInvoiceHeaderRowIndex(cleaned, 40);
  let itemColIdx = 0;
  let descColIdx = 1;
  if (headerIdx >= 0) {
    const headersNorm = (cleaned[headerIdx] || []).map(normalizeHeader);
    const cols = headerColumnIndices(headersNorm);
    itemColIdx = cols.itemIdx >= 0 ? cols.itemIdx : 0;
    descColIdx = cols.descIdx >= 0 ? cols.descIdx : 1;
  }

  const out: string[][] = [];
  for (let i = 0; i < cleaned.length; i += 1) {
    const row = cleaned[i];
    const joined = row.join(' ').replace(/\s+/g, ' ').trim();
    if (!joined) continue;

    if (i <= headerIdx) {
      out.push([...row]);
      continue;
    }

    const itemCell = (row[itemColIdx] || '').trim();
    const descCell = (row[descColIdx] || '').trim();
    const hasHs8 = /\b\d{8}\b/.test(joined);
    const isCombinedLine = /combined\s+nomenclature\s*:/i.test(joined);
    const shouldMergeToPrevious = Boolean(out.length > 0 && (isCombinedLine || (!itemCell && descCell)));
    if (shouldMergeToPrevious) {
      const prev = out[out.length - 1];
      const prevDesc = (prev[descColIdx] || '').trim();
      prev[descColIdx] = `${prevDesc} ${joined}`.replace(/\s+/g, ' ').trim();
      continue;
    }

    if (isPdfCurrencyOnlyLine(joined) && !hasHs8) continue;
    out.push([...row]);
  }
  return out;
}

function normalizeParsedPdfLineItems(rows: CustomsLineItem[]): CustomsLineItem[] {
  const out: CustomsLineItem[] = [];
  for (const row of rows) {
    const desc = String(row.description || '').replace(/\s+/g, ' ').trim();
    const isEmptyDesc = !desc || desc === '—' || desc === '-';
    const isCombinedOnly = /^combined\s+nomenclature\s*:/i.test(desc);
    const hasCommercialValues = (Number(row.net_eur) || 0) > 0 || (Number(row.qty) || 0) > 0;

    if (isEmptyDesc && !hasCommercialValues) {
      // Drop metadata ghosts (e.g. date/company number rows) that have no product text
      // and no commercial values.
      continue;
    }

    if (isCombinedOnly && out.length > 0 && !hasCommercialValues) {
      const prev = out[out.length - 1];
      const mergedDesc = `${String(prev.description || '').trim()} ${desc}`.replace(/\s+/g, ' ').trim();
      prev.description = mergedDesc;
      if ((!prev.eu_cn_8digit || !/^\d{8}$/.test(String(prev.eu_cn_8digit))) && /^\d{8}$/.test(String(row.eu_cn_8digit || ''))) {
        prev.eu_cn_8digit = String(row.eu_cn_8digit);
      }
      continue;
    }
    out.push(row);
  }
  return out;
}

type PdfInvoiceBlock = {
  itemCode: string;
  descriptionParts: string[];
  hs8: string;
  qty: number;
  unitPrice: number;
  net: number;
};

function parsePdfLineToBlockFields(lineRaw: string): {
  itemCode?: string;
  textWithoutNumbers: string;
  qty?: number;
  unitPrice?: number;
  net?: number;
  hs8?: string;
} {
  const line = String(lineRaw || '').replace(/\s+/g, ' ').trim();
  let rest = line;
  let itemCode: string | undefined;
  const itemMatch = rest.match(/\b([A-Z]{2}-ITM-\d{4})\b/i);
  if (itemMatch) {
    itemCode = itemMatch[1].toUpperCase();
    rest = rest.replace(itemMatch[0], ' ').replace(/\s+/g, ' ').trim();
  }

  // Common invoice tail: "<qty> £<unit_price> £<net_amount>".
  let qty: number | undefined;
  let unitPrice: number | undefined;
  let net: number | undefined;
  const tail = rest.match(
    /(\d+(?:[.,]\d+)?)\s+£?\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s+£?\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*$/i,
  );
  if (tail) {
    qty = toNumber(tail[1], Number.NaN);
    unitPrice = toNumber(tail[2], Number.NaN);
    net = toNumber(tail[3], Number.NaN);
    rest = rest.slice(0, tail.index).replace(/\s+/g, ' ').trim();
  }

  const hs8 = extractHs8FromText(rest);
  return {
    itemCode,
    textWithoutNumbers: rest,
    qty: Number.isFinite(qty ?? Number.NaN) ? qty : undefined,
    unitPrice: Number.isFinite(unitPrice ?? Number.NaN) ? unitPrice : undefined,
    net: Number.isFinite(net ?? Number.NaN) ? net : undefined,
    hs8: hs8 || undefined,
  };
}

/**
 * Parse PDF table by item blocks instead of generic row heuristics.
 * This preserves description text when PDF rows are split across multiple lines.
 */
function parsePdfMatrixAsInvoiceBlocks(matrix: string[][]): CustomsLineItem[] {
  const lines = matrix
    .map((row) => row.map((c) => String(c ?? '').trim()).filter(Boolean).join(' '))
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  if (!lines.length) return [];

  const afterHeaderStart = lines.findIndex((l) => /item\s*code/i.test(l) && /description/i.test(l));
  const body = afterHeaderStart >= 0 ? lines.slice(afterHeaderStart + 1) : lines;

  const blocks: PdfInvoiceBlock[] = [];
  let current: PdfInvoiceBlock | null = null;

  for (const line of body) {
    if (/^date\s*:|^your order\s*:|invoice ref\s*:|reg\.?\s*no/i.test(line)) continue;
    if (isPdfCurrencyOnlyLine(line)) continue;

    const parsed = parsePdfLineToBlockFields(line);
    const startsNewItem = Boolean(parsed.itemCode);
    const lineText = parsed.textWithoutNumbers;

    if (startsNewItem) {
      if (current) blocks.push(current);
      current = {
        itemCode: parsed.itemCode || '',
        descriptionParts: [],
        hs8: parsed.hs8 || '',
        qty: parsed.qty ?? 0,
        unitPrice: parsed.unitPrice ?? 0,
        net: parsed.net ?? 0,
      };
      if (lineText) current.descriptionParts.push(lineText);
      continue;
    }

    if (!current) continue;

    if (parsed.hs8 && !current.hs8) current.hs8 = parsed.hs8;
    if (parsed.qty != null && parsed.qty > 0 && current.qty <= 0) current.qty = parsed.qty;
    if (parsed.unitPrice != null && parsed.unitPrice > 0 && current.unitPrice <= 0) current.unitPrice = parsed.unitPrice;
    if (parsed.net != null && parsed.net > 0 && current.net <= 0) current.net = parsed.net;
    if (lineText) current.descriptionParts.push(lineText);
  }
  if (current) blocks.push(current);

  const rows = blocks
    .map((b, idx) => {
      const description = b.descriptionParts.join(' ').replace(/\s+/g, ' ').trim();
      const hs8 = b.hs8 || extractHs8FromText(description);
      if (!description && !hs8) return null;
      return {
        page: 1,
        item_code: b.itemCode || `ROW-${idx + 1}`,
        description,
        eu_cn_8digit: hs8,
        qty: b.qty || 0,
        unit: 'PC',
        unit_price_eur: b.unitPrice || 0,
        net_eur: b.net || 0,
        source: 'pdf_upload',
      } as CustomsLineItem;
    })
    .filter((row): row is CustomsLineItem => Boolean(row));

  return normalizeParsedPdfLineItems(rows);
}

/** Extract text rows from PDF pages, then reuse table/header heuristics used for CSV/XLSX. */
async function parsePdfArrayBuffer(buf: ArrayBuffer): Promise<CustomsLineItem[]> {
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(buf),
    useWorkerFetch: false,
    isEvalSupported: false,
  });
  const pdf = await loadingTask.promise;

  const matrix: string[][] = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const text = await page.getTextContent();
    const items = text.items as PdfTextItemLike[];
    const byColumns = extractPdfMatrixByColumns(items);
    if (byColumns.length > 0) {
      matrix.push(...byColumns);
      continue;
    }

    const byCoords = extractPdfTextLines(items);
    const byStream = extractPdfTextLinesByStream(items);
    const lines = byCoords.length >= byStream.length ? byCoords : byStream;

    for (const line of lines) matrix.push([line]);
  }
  const normalizedMatrix = normalizePdfInvoiceMatrix(matrix);
  const blockParsed = parsePdfMatrixAsInvoiceBlocks(normalizedMatrix);
  if (blockParsed.length >= 3) return blockParsed;
  const parsed = parseRowsToLineItems(normalizedMatrix, 'pdf_upload').filter(
    (row) => !(isPdfCurrencyOnlyLine(row.description) && (row.net_eur ?? 0) === 0 && (row.qty ?? 0) === 0),
  );
  return normalizeParsedPdfLineItems(parsed);
}

async function parsePdfViaPythonBackend(fileName: string, buf: ArrayBuffer): Promise<CustomsLineItem[]> {
  const res = await authenticatedFetch('/api/customs/extract-pdf-python', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/pdf',
      'X-Filename': encodeURIComponent(fileName || 'upload.pdf'),
    },
    body: buf,
  });
  const json = (await res.json()) as PythonPdfExtractResponse;
  if (!res.ok || !json.ok) {
    const msg = !json.ok ? `${json.error}${json.details ? `: ${json.details}` : ''}` : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const matrix = Array.isArray(json.matrix) ? json.matrix : [];
  if (!matrix.length) return [];
  const normalizedMatrix = normalizePdfInvoiceMatrix(matrix);
  const blockParsed = parsePdfMatrixAsInvoiceBlocks(normalizedMatrix);
  if (blockParsed.length >= 3) return blockParsed;
  const parsed = parseRowsToLineItems(normalizedMatrix, 'pdf_upload');
  return normalizeParsedPdfLineItems(parsed);
}

async function checkPythonPdfExtractorHealth(): Promise<PythonPdfHealthResponse | null> {
  try {
    const res = await authenticatedFetch('/api/customs/extract-pdf-python/health');
    const json = (await res.json()) as PythonPdfHealthResponse;
    return json;
  } catch {
    return null;
  }
}

function invoiceUploadFileBadge(name: string): string {
  const n = name.toLowerCase();
  if (n.endsWith('.csv')) return 'CSV';
  if (n.endsWith('.xlsx')) return 'XLSX';
  if (n.endsWith('.xls')) return 'XLS';
  if (n.endsWith('.pdf')) return 'PDF';
  return 'FILE';
}

function csvEscape(value: string | number | null | undefined) {
  const s = String(value ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toFriendlyError(input?: string) {
  const msg = String(input || '').toLowerCase();
  if (!msg) return 'Something went wrong. Please try again.';
  if (msg.includes('fetch failed') || msg.includes('failed to fetch') || msg.includes('network')) {
    return "We couldn't reach the tariff service. Try again in 30 seconds.";
  }
  if (msg.includes('404')) {
    return "We couldn't find tariff details for this code. Please try a different code.";
  }
  if (msg.includes('request failed')) {
    return 'The tariff service returned an error. Please try again in a moment.';
  }
  return input || 'Something went wrong. Please try again.';
}

/** Prefer non-…00 codes, deeper indent, stable code order (same as tariff tree “most specific”). */
function compareUkOptionsBestFirst(a: UkOption, b: UkOption): number {
  const aCode = String(a.code_10digit || '');
  const bCode = String(b.code_10digit || '');
  const aSpecific = aCode.endsWith('00') ? 0 : 1;
  const bSpecific = bCode.endsWith('00') ? 0 : 1;
  if (aSpecific !== bSpecific) return bSpecific - aSpecific;
  const aIndent = Number(a.number_indents ?? 0);
  const bIndent = Number(b.number_indents ?? 0);
  if (aIndent !== bIndent) return bIndent - aIndent;
  return aCode.localeCompare(bCode);
}

function pickBestUkOption(options: UkOption[] = []) {
  if (!options.length) return null;
  return [...options].sort(compareUkOptionsBestFirst)[0];
}

/** Sum weighted matches + how many distinct n-grams hit (for ranking ties). */
function scoreUkOptionForInvoiceDetailed(invoiceLine: string | undefined, opt: UkOption): { score: number; matches: number } {
  if (!invoiceLine?.trim()) return { score: 0, matches: 0 };
  const cleaned = cleanTariffDescription(opt.description || opt.formatted_description || '');
  if (!cleaned) return { score: 0, matches: 0 };
  const text = labelOtherFallback(cleaned);
  const ngrams = buildInvoiceNgrams(invoiceLine);
  let score = 0;
  let matches = 0;
  const seen = new Set<string>();
  for (const phrase of ngrams) {
    if (!flexMatchPhraseInTariff(text, phrase)) continue;
    const key = phrase.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    matches += 1;
    const words = phrase.split(/\s+/).filter(Boolean).length;
    score += phrase.length * Math.max(1, words);
  }
  return { score, matches };
}

function scoreUkOptionForInvoice(invoiceLine: string | undefined, opt: UkOption): number {
  return scoreUkOptionForInvoiceDetailed(invoiceLine, opt).score;
}

/**
 * Best description signal for an 8-digit HS across all invoice lines with that code
 * (avoids only using the first line; max score + tie-breakers).
 */
function aggregateDescScoreForHs8(items: CustomsLineItem[], code8: string, opt: UkOption): { score: number; matches: number; sumMatches: number } {
  const lines = items
    .filter((r) => r.eu_cn_8digit === code8 && r.description?.trim())
    .map((r) => r.description!.trim());
  if (lines.length === 0) return { score: 0, matches: 0, sumMatches: 0 };
  let bestScore = 0;
  let matchesAtBest = 0;
  let sumMatches = 0;
  for (const line of lines) {
    const { score, matches } = scoreUkOptionForInvoiceDetailed(line, opt);
    sumMatches += matches;
    if (score > bestScore) {
      bestScore = score;
      matchesAtBest = matches;
    } else if (score === bestScore) {
      matchesAtBest = Math.max(matchesAtBest, matches);
    }
  }
  return { score: bestScore, matches: matchesAtBest, sumMatches };
}

/** First non-empty line description for this 8-digit code (invoice + manual rows). */
function firstInvoiceDescriptionForHs8(items: CustomsLineItem[], hs8: string): string | undefined {
  const row = items.find((r) => r.eu_cn_8digit === hs8 && r.description?.trim());
  return row?.description?.trim();
}

/** When several UK 10-digit lines share the same 8-digit heading, require a clear description-based winner to auto-Ready. */
const AUTO_DESC_MATCH_MIN_SCORE = 12;
/** If every other option scores 0, allow a single strong token (e.g. “stroke”) to auto-Ready. */
const AUTO_DESC_MATCH_MIN_SCORE_SOLO_WINNER = 5;
const AUTO_DESC_MATCH_RATIO = 1.22;
const AUTO_DESC_MATCH_ABS_GAP = 6;

export default function CustomsAgent() {
  // Quick single-code HS lookup (same APIs as pre-redesign UI)
  const [lookupHs8, setLookupHs8] = useState('84819000');
  const [options, setOptions] = useState<UkOption[] | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const [selected10, setSelected10] = useState<string | null>(null);
  const [duty, setDuty] = useState<UkDutyResponse | null>(null);
  const [dutyLoading, setDutyLoading] = useState(false);

  // Document state
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedRows, setUploadedRows] = useState<CustomsLineItem[] | null>(null);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const [conversionRows, setConversionRows] = useState<ConversionResultRow[] | null>(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  // Redesign concept flow state (Upload -> Processing -> Review -> Export)
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [maxReachedStep, setMaxReachedStep] = useState<1 | 2 | 3 | 4>(1);
  const [manualAccordionOpen, setManualAccordionOpen] = useState(true);
  const [manualLineItems, setManualLineItems] = useState<ManualLineItem[]>([{ eu_cn_8digit: '' }]);
  const [pillTab, setPillTab] = useState<'all' | 'converted' | 'review'>('all');
  const [reviewTablePage, setReviewTablePage] = useState(1);
  const [reviewTableRowsPerPage, setReviewTableRowsPerPage] = useState(10);
  /** Persists after closing modals so users see which line they opened. Index in `previewRows`. */
  const [reviewTableFocusRowIndex, setReviewTableFocusRowIndex] = useState<number | null>(null);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [processingRunningStep, setProcessingRunningStep] = useState<1 | 2 | 3 | 4 | null>(null);
  const [startAgainConfirming, setStartAgainConfirming] = useState(false);

  // Duty/VAT for review table (keyed by chosen UK 10-digit code)
  const [dutyByCode10, setDutyByCode10] = useState<Record<string, UkDutyResponse | undefined>>({});
  // Step 4 export “generated” cache. We keep it in-memory only.
  const [exportCsvText, setExportCsvText] = useState<string | null>(null);

  /** Per 8-digit HS: chosen UK 10-digit in options modal (persists across open/close until Apply or reset). */
  const [ukOptionSelectionByHs8, setUkOptionSelectionByHs8] = useState<Record<string, string | null>>({});

  // Modal state
  const [modal, setModal] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
    data: unknown[];
    columns: ColumnDef[];
    footerNote?: string;
    isLoading?: boolean;
    emptyStateMessage?: string;
    /** When set, UK options modal uses checkbox column derived in render (not `columns`). */
    ukOptionsHs8?: string;
    ukOptionsInvoiceLine?: string;
  }>({
    open: false,
    title: '',
    data: [],
    columns: [],
    isLoading: false,
  });

  const manualPreviewRows = useMemo(() => {
    return manualLineItems
      .map((m, idx) => ({
        page: 1,
        item_code: `MANUAL-${idx + 1}`,
        description: '',
        eu_cn_8digit: m.eu_cn_8digit,
        qty: 1,
        unit: 'PC',
        unit_price_eur: 0,
        net_eur: 0,
        source: 'manual',
      }))
      .filter((r) => Boolean(r.eu_cn_8digit));
  }, [manualLineItems]);

  const previewRows = useMemo(() => {
    return [...(uploadedRows || []), ...manualPreviewRows];
  }, [uploadedRows, manualPreviewRows]);

  const uniqueHsCodes = useMemo(() => {
    const items = previewRows;
    return Array.from(new Set(items.map((i) => i.eu_cn_8digit))).sort();
  }, [previewRows]);

  /** Per line item (invoice row), not per distinct HS code — so All = Converted + Review. */
  const { convertedCount, needsAttentionCount } = useMemo(() => {
    if (!conversionRows) {
      return { convertedCount: 0, needsAttentionCount: 0 };
    }
    const byHs = new Map(conversionRows.map((r) => [r.hs8, r]));
    let converted = 0;
    let needsAttention = 0;
    for (const line of previewRows) {
      const conv = byHs.get(line.eu_cn_8digit);
      if (conv?.status === 'Ready') converted += 1;
      else needsAttention += 1;
    }
    return { convertedCount: converted, needsAttentionCount: needsAttention };
  }, [previewRows, conversionRows]);

  const filteredReviewRows = useMemo(() => {
    const rowsByHs = new Map((conversionRows || []).map((r) => [r.hs8, r]));
    return previewRows
      .map((r, idx) => {
        const conv = rowsByHs.get(r.eu_cn_8digit);
        const status: 'Ready' | 'Needs attention' = conv?.status ?? 'Needs attention';
        return { r, idx, conv, status };
      })
      .filter((x) => {
        if (pillTab === 'converted') return x.status === 'Ready';
        if (pillTab === 'review') return x.status !== 'Ready';
        return true;
      });
  }, [previewRows, conversionRows, pillTab]);

  const reviewTableTotalPages = useMemo(() => {
    const pages = Math.ceil(filteredReviewRows.length / reviewTableRowsPerPage);
    return Math.max(1, pages);
  }, [filteredReviewRows.length, reviewTableRowsPerPage]);

  const pagedReviewRows = useMemo(() => {
    const start = (reviewTablePage - 1) * reviewTableRowsPerPage;
    return filteredReviewRows.slice(start, start + reviewTableRowsPerPage);
  }, [filteredReviewRows, reviewTablePage, reviewTableRowsPerPage]);

  useEffect(() => {
    setReviewTablePage(1);
  }, [pillTab, previewRows.length, conversionRows]);

  useEffect(() => {
    if (reviewTablePage > reviewTableTotalPages) {
      setReviewTablePage(reviewTableTotalPages);
    }
  }, [reviewTablePage, reviewTableTotalPages]);

  const convertHsCodes = async (codes: string[], lineItems: CustomsLineItem[]): Promise<ConversionResultRow[]> => {
    const uniqueCodes = Array.from(new Set(codes.map((c) => c.trim()).filter(isValid8)));
    if (uniqueCodes.length === 0) {
      setConversionRows([]);
      setConversionError('No valid 8-digit HS codes found in extracted rows.');
      return [];
    }

    setConversionLoading(true);
    setConversionError(null);
    setConversionRows(null);

    try {
      const rows = await Promise.all(
        uniqueCodes.map(async (code8): Promise<ConversionResultRow> => {
          try {
            const res = await authenticatedFetch(`/api/customs/uk/options?code8=${encodeURIComponent(code8)}`);
            const json = (await res.json()) as UkOptionsResponse;
            if (!res.ok || !json.ok) throw new Error(!json.ok ? json.error : `Request failed (${res.status})`);
            const allOptions = json.options || [];
            const non00Options = allOptions.filter((o) => !String(o.code_10digit || '').endsWith('00'));
            const candidateOptions = non00Options.length > 0 ? non00Options : allOptions;
            const hasAnyInvoiceDesc = lineItems.some((r) => r.eu_cn_8digit === code8 && r.description?.trim());
            const scored = candidateOptions
              .map((o) => {
                const agg = aggregateDescScoreForHs8(lineItems, code8, o);
                return { o, s: agg.score, m: agg.matches, sm: agg.sumMatches };
              })
              .sort((a, b) => {
                if (b.s !== a.s) return b.s - a.s;
                if (b.m !== a.m) return b.m - a.m;
                if (b.sm !== a.sm) return b.sm - a.sm;
                return compareUkOptionsBestFirst(a.o, b.o);
              });
            const best = scored[0]?.o ?? pickBestUkOption(candidateOptions);
            const bestScore = scored[0]?.s ?? 0;
            const secondScore = scored[1]?.s ?? 0;
            const hasMultiple = candidateOptions.length > 1;

            const minScoreForAuto =
              secondScore === 0 ? AUTO_DESC_MATCH_MIN_SCORE_SOLO_WINNER : AUTO_DESC_MATCH_MIN_SCORE;

            let status: 'Ready' | 'Needs attention';
            if (!best) {
              status = 'Needs attention';
            } else if (!hasMultiple) {
              status = 'Ready';
            } else if (hasAnyInvoiceDesc && bestScore >= minScoreForAuto) {
              const clearWinner =
                secondScore === 0 ||
                bestScore >= secondScore * AUTO_DESC_MATCH_RATIO ||
                bestScore - secondScore >= AUTO_DESC_MATCH_ABS_GAP;
              status = clearWinner ? 'Ready' : 'Needs attention';
            } else {
              status = 'Needs attention';
            }

            return {
              hs8: code8,
              options_found: json.count || 0,
              top_code10: best?.code_10digit || '-',
              top_description: cleanTariffDescription(best?.description || '') || '-',
              status,
              desc_best_score: bestScore,
              desc_second_score: hasMultiple ? secondScore : 0,
              candidate_count: candidateOptions.length,
            };
          } catch {
            return {
              hs8: code8,
              options_found: 0,
              top_code10: '-',
              top_description: '-',
              status: 'Needs attention',
              desc_best_score: 0,
              desc_second_score: 0,
              candidate_count: 0,
            };
          }
        }),
      );
      setConversionRows(rows);
      return rows;
    } catch (e) {
      setConversionError(toFriendlyError(e instanceof Error ? e.message : 'HS conversion failed'));
      return [];
    } finally {
      setConversionLoading(false);
    }
  };

  const applyManualChoice = (hs8: string, selected: UkOption) => {
    const inv = firstInvoiceDescriptionForHs8(previewRows, hs8);
    const pickedDescScore = scoreUkOptionForInvoice(inv, selected);
    setConversionRows((prev) =>
      (prev || []).map((row) =>
        row.hs8 === hs8
          ? {
              ...row,
              top_code10: selected.code_10digit,
              top_description: cleanTariffDescription(selected.description) || '-',
              status: 'Ready',
              desc_best_score: Math.max(pickedDescScore, row.desc_best_score ?? 0, 28),
              desc_second_score: 0,
              candidate_count: 1,
            }
          : row,
      ),
    );
    setModal((m) => ({
      ...m,
      open: false,
      ukOptionsHs8: undefined,
      ukOptionsInvoiceLine: undefined,
    }));

    // If the user picks a specific option, populate duty/VAT for it (so the review table stays consistent).
    const code10 = selected.code_10digit;
    void authenticatedFetch(`/api/customs/uk/duty?code10=${encodeURIComponent(code10)}`)
      .then(async (res) => {
        const json = (await res.json()) as UkDutyResponse;
        return { ok: res.ok, json };
      })
      .then(({ ok, json }) => {
        if (!ok || !json.ok) throw new Error(!json.ok ? json.error : 'Duty lookup failed');
        setDutyByCode10((prev) => ({ ...prev, [code10]: json }));
      })
      .catch((e) => {
        setDutyByCode10((prev) => ({ ...prev, [code10]: { ok: false, error: toFriendlyError(e instanceof Error ? e.message : 'Duty lookup failed') } }));
      });
  };

  const buildOutputCsvText = (): string | null => {
    if (!conversionRows || conversionRows.length === 0) return null;

    const rowsByHs = new Map(conversionRows.map((r) => [r.hs8, r]));
    const header = [
      'page',
      'item_code',
      'description',
      'hs_8digit',
      'qty',
      'unit',
      'net_eur',
      'uk_10digit_options',
      'uk_top_10digit',
      'uk_top_description',
      'DUTY_%',
      'VAT_%',
      'conversion_status',
    ];

    const lines = [header.join(',')];
    for (const r of previewRows) {
      const conv = rowsByHs.get(r.eu_cn_8digit);
      const duty = conv?.top_code10 ? dutyByCode10[conv.top_code10] : undefined;
      lines.push(
        [
          r.page,
          r.item_code,
          r.description,
          r.eu_cn_8digit,
          r.qty,
          r.unit,
          r.net_eur,
          conv?.options_found ?? '',
          conv?.top_code10 ?? '',
          conv?.top_description ?? '',
          duty?.ok ? Number(duty.duty_pct).toFixed(1) : '',
          duty?.ok ? Number(duty.vat_pct).toFixed(0) : '',
          conv?.status ?? '',
        ]
          .map(csvEscape)
          .join(','),
      );
    }

    return lines.join('\n');
  };

  const downloadOutputCsv = () => {
    if (!conversionRows || conversionRows.length === 0) {
      setUploadNotice('No conversion output yet. Upload CSV and wait for conversion first.');
      return;
    }

    const csvText = exportCsvText ?? buildOutputCsvText();
    if (!csvText) return;

    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customs_output_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateExportCsvText = () => {
    const text = buildOutputCsvText();
    setExportCsvText(text);
    return text;
  };

  const runLookup = async () => {
    const code8 = lookupHs8.trim();
    if (!isValid8(code8)) {
      setOptionsError('Enter a valid 8-digit HS code (numbers only).');
      setOptions(null);
      return;
    }

    setOptionsLoading(true);
    setOptionsError(null);
    setOptions(null);
    setSelected10(null);
    setDuty(null);

    try {
      const res = await authenticatedFetch(`/api/customs/uk/options?code8=${encodeURIComponent(code8)}`);
      const json = (await res.json()) as UkOptionsResponse;
      if (!res.ok || !json.ok) {
        throw new Error(!json.ok ? json.error : `Request failed (${res.status})`);
      }
      setOptions(json.options || []);
    } catch (e) {
      setOptionsError(toFriendlyError(e instanceof Error ? e.message : 'Failed to fetch tariff options'));
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchDuty = async (code10: string) => {
    setSelected10(code10);
    setDutyLoading(true);
    setDuty(null);
    try {
      const res = await authenticatedFetch(`/api/customs/uk/duty?code10=${encodeURIComponent(code10)}`);
      const json = (await res.json()) as UkDutyResponse;
      if (!res.ok || !json.ok) {
        throw new Error(!json.ok ? json.error : `Request failed (${res.status})`);
      }
      setDuty(json);
    } catch (e) {
      setDuty({ ok: false, error: toFriendlyError(e instanceof Error ? e.message : 'Failed to fetch duty') });
    } finally {
      setDutyLoading(false);
    }
  };

  const openInvoiceModal = () => {
    const rows = (uploadedRows || []) as CustomsLineItem[];
    if (!rows.length) return;
    setModal({
      open: true,
      title: 'Uploaded Invoice Line Items',
      subtitle: `${rows.length} rows from "${uploadedFileName || 'uploaded file'}"`,
      data: rows,
      columns: invoiceColumns,
      isLoading: false,
      emptyStateMessage: undefined,
      ukOptionsHs8: undefined,
      ukOptionsInvoiceLine: undefined,
    });
  };

  const openReviewOptionsModalForRow = (code8: string, invoiceLineDescription: string | undefined, rowIndex: number) => {
    setReviewTableFocusRowIndex(rowIndex);
    void openOptionsModalForCode(code8, invoiceLineDescription);
  };

  const openOptionsModalForCode = async (code8: string, invoiceLineDescription?: string) => {
    setModal({
      open: true,
      title: `HS ${code8} → UK 10-digit options`,
      subtitle: 'Loading…',
      data: [],
      columns: [],
      footerNote: UK_TRADE_TARIFF_SOURCE,
      isLoading: true,
      emptyStateMessage: undefined,
      ukOptionsHs8: code8,
      ukOptionsInvoiceLine: invoiceLineDescription,
    });
    try {
      const res = await authenticatedFetch(`/api/customs/uk/options?code8=${encodeURIComponent(code8)}`);
      const json = (await res.json()) as UkOptionsResponse;
      if (!res.ok || !json.ok) throw new Error(!json.ok ? json.error : `Request failed (${res.status})`);
      const options = json.options || [];
      const noMatches = options.length === 0;
      setModal({
        open: true,
        title: `HS ${code8} → UK 10-digit options`,
        subtitle: noMatches
          ? 'No matching UK Trade Tariff lines for this 8-digit code'
          : `${json.count} option${json.count === 1 ? '' : 's'} · select a row · Apply selection to confirm`,
        data: options,
        columns: [],
        footerNote: UK_TRADE_TARIFF_SOURCE,
        isLoading: false,
        emptyStateMessage: noMatches ? UK_OPTIONS_NO_MATCH_MESSAGE : undefined,
        ukOptionsHs8: code8,
        ukOptionsInvoiceLine: invoiceLineDescription,
      });
    } catch (e) {
      setModal({
        open: true,
        title: `HS ${code8} → UK 10-digit options`,
        subtitle: toFriendlyError(e instanceof Error ? e.message : 'Failed to fetch options'),
        data: [],
        columns: [],
        footerNote: UK_TRADE_TARIFF_SOURCE,
        isLoading: false,
        emptyStateMessage: undefined,
        ukOptionsHs8: code8,
        ukOptionsInvoiceLine: invoiceLineDescription,
      });
    }
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const extractedLineItemsSectionRef = useRef<HTMLDivElement | null>(null);

  // Quick action from Agents landing page: jump to upload and open file picker.
  useEffect(() => {
    try {
      const flag = localStorage.getItem('agents_quick_action_customs_upload');
      if (!flag) return;
      localStorage.removeItem('agents_quick_action_customs_upload');
      // Ensure we're at the upload step.
      setCurrentStep(1);
      setMaxReachedStep((s) => (s < 1 ? 1 : s));
      // Scroll to the upload zone and open the file picker.
      setTimeout(() => {
        const dz = document.getElementById('dropZone');
        dz?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => fileInputRef.current?.click(), 250);
      }, 50);
    } catch {
      // ignore
    }
     
  }, []);

  // Lightweight "last result" tracking for Agents landing page (best-effort).
  useEffect(() => {
    try {
      if (!uploadedRows || uploadedRows.length === 0) return;
      localStorage.setItem('agents_last_result_customs_ts', new Date().toISOString());
      localStorage.setItem('agents_last_result_customs_items', String(uploadedRows.length));
    } catch {
      // ignore
    }
  }, [uploadedRows]);

  // Store conversion outcome counts for Agents landing page (best-effort).
  useEffect(() => {
    try {
      if (!conversionRows || conversionRows.length === 0) return;
      const converted = conversionRows.filter((r) => r.status === 'Ready').length;
      const attention = conversionRows.filter((r) => r.status !== 'Ready').length;
      localStorage.setItem('agents_last_result_customs_converted', String(converted));
      localStorage.setItem('agents_last_result_customs_attention', String(attention));
    } catch {
      // ignore
    }
  }, [conversionRows]);

  const resetAll = () => {
    setUploadedFileName(null);
    setUploadedRows(null);
    setUploadNotice(null);

    setConversionRows(null);
    setConversionError(null);
    setConversionLoading(false);

    setDutyByCode10({});
    setExportConfirmOpen(false);
    setExportCsvText(null);
    setPillTab('all');
    setReviewTableFocusRowIndex(null);
    setUkOptionSelectionByHs8({});

    setProcessingRunningStep(null);
    setStartAgainConfirming(false);
    setManualAccordionOpen(true);
    setManualLineItems([{ eu_cn_8digit: '' }]);

    setLookupHs8('84819000');
    setOptions(null);
    setOptionsError(null);
    setOptionsLoading(false);
    setSelected10(null);
    setDuty(null);
    setDutyLoading(false);

    setModal((m) => ({ ...m, open: false }));
    setExportConfirmOpen(false);
    setCurrentStep(1);
    setMaxReachedStep(1);
  };

  const goStep = (n: 1 | 2 | 3 | 4) => {
    setCurrentStep(n);
    setMaxReachedStep((prev) => (n > prev ? n : prev));
    if (n !== 3) setExportConfirmOpen(false);
  };

  /** Tab content scrolls inside App’s overflow container — use scrollIntoView, not window.scrollTo. */
  const scrollCustomsPageTopSmooth = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById('customs-agent-page-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const scrollToUploadSectionSmooth = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById('customs-agent-upload-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  // Mirrors the concept behavior: allow navigating only to steps reached; never back to step 2.
  const stepClick = (n: 1 | 2 | 3 | 4) => {
    if (n === currentStep) return;
    if (n > maxReachedStep) return;
    if (n === 2) return;
    goStep(n);
  };

  const handleInvoiceFile = async (f: File) => {
    setUploadNotice(null);

    // New input invalidates prior conversion/export results.
    setConversionRows(null);
    setConversionError(null);
    setDutyByCode10({});
    setExportConfirmOpen(false);
    setExportCsvText(null);
    setPillTab('all');
    setReviewTableFocusRowIndex(null);
    setUkOptionSelectionByHs8({});

    setUploadedFileName(f.name);

    const lowerName = f.name.toLowerCase();
    const isCsv = lowerName.endsWith('.csv') || f.type.includes('csv');
    const isXlsx =
      lowerName.endsWith('.xlsx') ||
      lowerName.endsWith('.xls') ||
      f.type.includes('spreadsheetml') ||
      f.type === 'application/vnd.ms-excel';
    const isPdf = lowerName.endsWith('.pdf') || f.type.includes('pdf');

    if (isCsv) {
      try {
        const csvText = await f.text();
        const rows = parseCsvToLineItems(csvText);
        setUploadedRows(rows.length > 0 ? rows : []);
        setUploadNotice(
          rows.length > 0
            ? `✓ Loaded ${rows.length} row${rows.length === 1 ? '' : 's'} from ${f.name}.`
            : 'CSV uploaded, but no rows were extracted. Check columns (description, HS code, qty, amount).',
        );
      } catch {
        setUploadedRows([]);
        setUploadNotice('We could not read this CSV file. Please use a valid UTF-8 CSV.');
      }
      return;
    }

    if (isXlsx) {
      try {
        const buf = await f.arrayBuffer();
        const rows = parseXlsxArrayBuffer(buf);
        setUploadedRows(rows.length > 0 ? rows : []);
        setUploadNotice(
          rows.length > 0
            ? `✓ Loaded ${rows.length} row${rows.length === 1 ? '' : 's'} from ${f.name}.`
            : 'Spreadsheet uploaded, but no rows were extracted. Use a table with headers (description, HS code, qty, amount) or clear line data.',
        );
      } catch {
        setUploadedRows([]);
        setUploadNotice('We could not read this Excel file. Try saving as .xlsx and upload again.');
      }
      return;
    }

    if (isPdf) {
      try {
        const buf = await f.arrayBuffer();
        let rows: CustomsLineItem[] = [];
        let usedPythonFallback = false;
        let browserParseFailed = false;

        try {
          rows = await parsePdfArrayBuffer(buf);
        } catch {
          browserParseFailed = true;
          rows = [];
        }

        if (!rows.length) {
          rows = await parsePdfViaPythonBackend(f.name, buf);
          usedPythonFallback = rows.length > 0;
        }

        setUploadedRows(rows.length > 0 ? rows : []);
        if (rows.length > 0) {
          setUploadNotice(
            usedPythonFallback || browserParseFailed
              ? `✓ Loaded ${rows.length} row${rows.length === 1 ? '' : 's'} from ${f.name} (Python extractor).`
              : `✓ Loaded ${rows.length} row${rows.length === 1 ? '' : 's'} from ${f.name}.`,
          );
        } else {
          setUploadNotice('PDF uploaded, but no line items were extracted. Use a clearer PDF, CSV, or .xlsx.');
        }
      } catch (e) {
        setUploadedRows([]);
        const raw = String(e instanceof Error ? e.message : '').toLowerCase();
        if (raw.includes('modulenotfounderror') || raw.includes('pdfplumber')) {
          setUploadNotice('Python PDF fallback is unavailable (missing pdfplumber). Install server Python deps and retry.');
          return;
        }
        const health = await checkPythonPdfExtractorHealth();
        if (health && health.ok === false) {
          const details = String(health.details || '').toLowerCase();
          if (details.includes('pdfplumber')) {
            setUploadNotice('Python PDF fallback is unavailable (missing pdfplumber). Install server Python deps and retry.');
            return;
          }
          setUploadNotice(`Python PDF fallback is unavailable: ${health.error || 'health check failed'}.`);
          return;
        }
        setUploadNotice('We could not read this PDF. Try re-saving it as PDF, or upload CSV/.xlsx.');
      }
      return;
    }

    setUploadedRows([]);
    setUploadNotice('Unsupported file type. Please upload a PDF, CSV, or Excel (.xlsx) file.');
  };

  const startProcessing = async () => {
    setExportConfirmOpen(false);
    setExportCsvText(null);
    setPillTab('all');
    setReviewTableFocusRowIndex(null);
    setUkOptionSelectionByHs8({});
    setConversionError(null);
    setDutyByCode10({});
    setModal((m) => ({ ...m, open: false }));

    if (!previewRows.length) {
      setUploadNotice('Upload a CSV or Excel file, or enter HS codes above before starting.');
      return;
    }

    // Step 2 UI starts immediately while async conversion runs.
    goStep(2);
    setProcessingRunningStep(1);
    const timers: number[] = [];
    timers.push(window.setTimeout(() => setProcessingRunningStep(2), 900));
    timers.push(window.setTimeout(() => setProcessingRunningStep(3), 1800));
    timers.push(window.setTimeout(() => setProcessingRunningStep(4), 2700));

    try {
      const rows = await convertHsCodes(uniqueHsCodes, previewRows);
      if (!rows.length) {
        setProcessingRunningStep(null);
        goStep(1);
        scrollToUploadSectionSmooth();
        return;
      }

      // Fetch duty/VAT for chosen UK codes so the review table can render instantly.
      const code10s = Array.from(
        new Set(rows.map((r) => r.top_code10).filter((c) => typeof c === 'string' && c && c !== '-')),
      );

      const dutyEntries = await Promise.all(
        code10s.map(async (code10) => {
          try {
            const res = await authenticatedFetch(`/api/customs/uk/duty?code10=${encodeURIComponent(code10)}`);
            const json = (await res.json()) as UkDutyResponse;
            if (!res.ok || !json.ok) throw new Error(!json.ok ? json.error : `Request failed (${res.status})`);
            return [code10, json] as const;
          } catch (e) {
            return [
              code10,
              { ok: false, error: toFriendlyError(e instanceof Error ? e.message : 'Duty lookup failed') } as UkDutyResponse,
            ] as const;
          }
        }),
      );

      setDutyByCode10(Object.fromEntries(dutyEntries));

      setProcessingRunningStep(4);
      goStep(3);
      scrollCustomsPageTopSmooth();
    } finally {
      timers.forEach((t) => window.clearTimeout(t));
      setProcessingRunningStep(null);
    }
  };

  const formatEur = (n: number) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return `€${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const goToExtractedLineItemsTab = (tab: 'all' | 'converted' | 'review') => {
    setPillTab(tab);
    setReviewTablePage(1);
    window.requestAnimationFrame(() => {
      extractedLineItemsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const ReviewTable = ({
    rowsToRender,
    dutyByCode10: dutyMap,
    focusedRowIndex,
    onOpenUkOptions,
  }: {
    rowsToRender: Array<{
      r: CustomsLineItem;
      idx: number;
      conv: ConversionResultRow | undefined;
      status: 'Ready' | 'Needs attention';
    }>;
    dutyByCode10: Record<string, UkDutyResponse | undefined>;
    focusedRowIndex: number | null;
    onOpenUkOptions: (hs8: string, invoiceLineDescription: string | undefined, rowIndex: number) => void;
  }) => {
    return (
      <table className="rt">
        <thead>
          <tr>
            <th>#</th>
            <th>Description</th>
            <th>EU Code → UK Code</th>
            <th>Value (EUR)</th>
            <th>Duty</th>
            <th>VAT</th>
            <th>Confidence</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rowsToRender.map(({ r, idx, conv, status }) => {
            const hs8 = r.eu_cn_8digit;
            const code10 = conv?.top_code10 ?? '-';
            const optionsFound = conv?.options_found ?? 0;
            const duty = code10 && code10 !== '-' ? dutyMap[code10] : undefined;

            const needsReview = status !== 'Ready';

            const { pct: confValue, tier: confTier } = computeUkLineConfidence({
              hs8,
              lineDescription: r.description,
              conv,
              status,
            });

            const invalidOrNoOptions = !isValid8(hs8) || optionsFound === 0;
            /** Red: bad/missing HS or no UK lines. Amber: real heading with options but not Ready. Green: Ready (computed tier). */
            const confTierDisplay =
              status === 'Ready'
                ? confTier
                : invalidOrNoOptions
                  ? 'lo'
                  : 'med';
            const ukCell =
              status === 'Ready' && code10 !== '-' ? (
                renderUkCode10EmeraldSuffix(code10)
              ) : invalidOrNoOptions ? (
                <span className="mono-code invalid">Invalid conversion</span>
              ) : optionsFound === 1 ? (
                <span className="mono-code warn">Single option</span>
              ) : (
                <span className="mono-code warn">{`${optionsFound} options`}</span>
              );

            const dutyText =
              status === 'Ready' && duty?.ok ? `${Number(duty.duty_pct).toFixed(1)}%` : '—';
            const vatText = status === 'Ready' && duty?.ok ? `${Number(duty.vat_pct).toFixed(0)}%` : '—';

            return (
              <tr
                key={idx}
                className={[needsReview ? 'flagged' : '', focusedRowIndex === idx ? 'rt-row-focused' : ''].filter(Boolean).join(' ')}
              >
                <td className="rt-row-num">{String(idx + 1).padStart(2, '0')}</td>
                <td className="rt-desc-cell">{formatTariffDescriptionPlain(r.description)}</td>
                <td>
                  <span className="mono-code">{hs8}</span>
                  <span className="arr">→</span>
                  {ukCell}
                </td>
                <td>{formatEur(r.net_eur)}</td>
                <td>{dutyText}</td>
                <td>{vatText}</td>
                <td>
                  <span
                    className={`conf ${confTierDisplay}`}
                    title="Colour: red = invalid HS or no UK tariff lines; amber = valid heading with options to choose; green = Ready. Score reflects text match and option separation — not duty or VAT."
                  >
                    <span className="dot" />
                    {confValue}%
                  </span>
                </td>
                <td>
                  <div className="rt-actions-cell">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className={`border bg-card ${
                        needsReview
                          ? 'border-warning/30 text-warning hover:bg-warning/10'
                          : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                      }`}
                      onClick={() => onOpenUkOptions(hs8, r.description, idx)}
                      title="All UK 10-digit codes from the UK Trade Tariff."
                    >
                      {needsReview ? 'Review ⚠' : 'Review options'}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
          {rowsToRender.length === 0 && (
            <tr>
              <td colSpan={8} style={{ color: '#6b7280', padding: '20px 18px' }}>
                No items to display.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const ukOptionsHs8 = modal.ukOptionsHs8;
  const selectedCode10ForUkModal =
    ukOptionsHs8 != null && ukOptionsHs8 !== ''
      ? getUkOptionSelectionForHs8(ukOptionsHs8, ukOptionSelectionByHs8, conversionRows)
      : null;

  const applyUkOptionsFromModal = () => {
    const hs8 = modal.ukOptionsHs8;
    if (!hs8) return;
    const code10 = getUkOptionSelectionForHs8(hs8, ukOptionSelectionByHs8, conversionRows);
    if (!code10) return;
    const row = (modal.data as UkOption[]).find((o) => String(o.code_10digit) === code10);
    if (!row) return;
    setUkOptionSelectionByHs8((prev) => {
      const next = { ...prev };
      delete next[hs8];
      return next;
    });
    applyManualChoice(hs8, row);
  };

  const kpiModalColumns =
    ukOptionsHs8 != null && ukOptionsHs8 !== ''
      ? buildTariffOptionsCheckboxColumns(
          modal.ukOptionsInvoiceLine,
          selectedCode10ForUkModal,
          ukOptionsHs8,
          (code10) =>
            setUkOptionSelectionByHs8((prev) => ({
              ...prev,
              [ukOptionsHs8]: code10,
            })),
        )
      : modal.columns;

  const ukOptionsModalFooter =
    ukOptionsHs8 != null &&
    ukOptionsHs8 !== '' &&
    !modal.isLoading &&
    (modal.data?.length ?? 0) > 0 &&
    !modal.emptyStateMessage ? (
      <Button
        type="button"
        variant="default"
        size="md"
        disabled={!selectedCode10ForUkModal}
        onClick={applyUkOptionsFromModal}
        className="font-semibold"
      >
        Apply selection
      </Button>
    ) : null;

  return (
    <div className="customs-agent" id="customs-agent-page-top">
      {/* Header */}
      <div className="ca-header">
        <div className="ca-header-left">
          <div className="ca-logo">ZF</div>
          <div>
            <div className="ca-header-title">ZEEFLEET Document Agent</div>
          </div>
        </div>
        <div className="ca-header-right">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={`${currentStep > 1 ? '' : 'hidden'} text-muted-foreground ${
              startAgainConfirming
                ? 'border-destructive text-destructive'
                : 'hover:border-destructive hover:text-destructive hover:bg-destructive/5'
            }`}
            onClick={() => {
              if (startAgainConfirming) {
                resetAll();
                return;
              }
              setStartAgainConfirming(true);
              setTimeout(() => setStartAgainConfirming(false), 3000);
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-4.5" />
            </svg>
            {startAgainConfirming ? 'Sure? Click again to confirm' : 'Start again'}
          </Button>
        </div>
      </div>

      {/* Step progress */}
      <div className="progress-wrap">
        <div className="progress-bar">
          {([1, 2, 3, 4] as const).map((n, idx) => {
            const isActive = currentStep === n;
            const isComplete = n < currentStep;
            const circleText = isComplete ? '✓' : n;
            return (
              <div key={n} className="stepWrapper" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div
                  className={`step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}
                  onClick={() => stepClick(n)}
                >
                  <div className="step-circle">{circleText}</div>
                  <div className="step-label">
                    {n === 1 ? 'Upload' : n === 2 ? 'Processing' : n === 3 ? 'Review' : 'Export'}
                  </div>
                </div>
                {idx < 3 && (
                  <div className={`connector ${n < currentStep ? 'done' : ''}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="ca-main">
        {/* Step 1: Upload */}
        <div className={`step-panel ${currentStep === 1 ? 'active' : ''}`} id="p1">
          {/* Quick HS lookup — same backend flow as pre-redesign (options → duty/VAT → gov.uk link) */}
          <div className="ca-lookup-card">
            <div className="ca-lookup-title">Quick HS code lookup</div>
            <div className="ca-lookup-sub">
              Enter an 8-digit EU/commodity code to list UK 10-digit matches, then select one for duty, VAT, and the official GOV.UK commodity page.
            </div>
            <div className="ca-data-source-line">{UK_TRADE_TARIFF_SOURCE_LINE}</div>
            <div className="ca-lookup-row">
              <input
                className="ca-lookup-input"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="00000000"
                maxLength={8}
                value={lookupHs8}
                onChange={(e) => setLookupHs8(e.target.value.replace(/\D/g, '').slice(0, 8))}
              />
              <Button type="button" variant="default" size="md" loading={optionsLoading} onClick={() => void runLookup()}>
                Look up
              </Button>
              {options !== null && !optionsLoading && (
                <span className="ca-lookup-options-count" aria-live="polite">
                  ({options.length} possible option{options.length === 1 ? '' : 's'})
                </span>
              )}
            </div>
            {optionsError && <div className="ca-lookup-err">{optionsError}</div>}
            {options && options.length > 0 && (
              <div className="ca-lookup-options" role="list">
                {options.map((opt) => {
                  const c10 = String(opt.code_10digit || '');
                  const on = selected10 === c10;
                  return (
                    <Button
                      key={c10}
                      type="button"
                      role="listitem"
                      variant="secondary"
                      size="md"
                      className={`ca-lookup-opt h-auto items-start justify-start ${on ? 'on' : ''}`}
                      onClick={() => void fetchDuty(c10)}
                    >
                      <div className="ca-lookup-opt-code">{renderUkCode10EmeraldSuffix(c10)}</div>
                      <div className="ca-lookup-desc">{renderQuickLookupOptionDescription(opt.description, options)}</div>
                    </Button>
                  );
                })}
              </div>
            )}
            {options && options.length === 0 && !optionsLoading && !optionsError && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>No UK options returned for this code.</div>
            )}
            {dutyLoading && <div style={{ fontSize: 12, color: '#6b7280' }}>Loading duty &amp; VAT…</div>}
            {duty && !dutyLoading && (
              <div className="ca-lookup-duty">
                {duty.ok ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <strong>Duty (MFN):</strong> {Number(duty.duty_pct).toFixed(1)}% &nbsp;·&nbsp; <strong>VAT (UK):</strong>{' '}
                      {Number(duty.vat_pct).toFixed(0)}%
                    </div>
                    {duty.description && (
                      <div className="ca-lookup-desc ca-lookup-desc--duty">{formatTariffDescriptionPlain(duty.description)}</div>
                    )}
                    {duty.commodity_url ? (
                      <a href={duty.commodity_url} target="_blank" rel="noopener noreferrer">
                        Open commodity page on GOV.UK ↗
                      </a>
                    ) : null}
                  </>
                ) : (
                  <span style={{ color: '#f87171' }}>{duty.error || 'Duty lookup failed.'}</span>
                )}
              </div>
            )}
          </div>

          <div className="section-title" id="customs-agent-upload-section">
            Start with your supplier invoice
          </div>
          <div className="section-sub">Upload a PDF, CSV, or Excel (.xlsx), or enter HS codes directly — we'll handle the rest.</div>

          <div
            className={`upload-zone ${uploadedFileName ? 'has-file' : ''}`}
            id="dropZone"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) void handleInvoiceFile(f);
            }}
            role="button"
            tabIndex={0}
          >
            <div className="upload-icon-wrap">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
            </div>
            <div className="upload-h">Drop your supplier invoice here</div>
            <div className="upload-p">Drag and drop, or click to browse — works with machine-readable and scanned documents</div>
            <Button
              type="button"
              variant="default"
              size="md"
              className="shadow-lg shadow-primary/25 hover:shadow-primary/35"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Choose file
            </Button>

            <div className="format-row">
              <span className="fmt">PDF</span>
              <span className="fmt">CSV</span>
              <span className="fmt">XLSX</span>
              <span className="fmt-note">&nbsp;Multi-page invoices supported · OCR for scanned docs</span>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf,text/csv,.csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleInvoiceFile(f);
            }}
          />

          <div className={`file-chip ${uploadedFileName ? 'show' : ''}`} id="fileChip">
            <div className="file-type-badge">{uploadedFileName ? invoiceUploadFileBadge(uploadedFileName) : 'PDF'}</div>
            <div className="file-chip-info">
              <div className="file-chip-name">{uploadedFileName || '-'}</div>
              <div className="file-chip-meta">{previewRows.length} line items detected</div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="remove-file h-auto"
              onClick={() => {
                setUploadedFileName(null);
                setUploadedRows(null);
                setUploadNotice(null);
                // clear conversion, since we have new input set
                setConversionRows(null);
                setDutyByCode10({});
                setExportConfirmOpen(false);
                setExportCsvText(null);
                setReviewTableFocusRowIndex(null);
                setUkOptionSelectionByHs8({});
                setCurrentStep(1);
                setMaxReachedStep(1);
              }}
            >
              ✕ Remove
            </Button>
          </div>
          {uploadedFileName && uploadedRows && uploadedRows.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Button type="button" variant="link" size="sm" className="px-0 no-underline text-primary hover:underline" onClick={openInvoiceModal}>
                View all uploaded lines ({uploadedRows.length})
              </Button>
            </div>
          )}

          <div className="or-row">
            <span>OR ENTER CODES MANUALLY</span>
          </div>

          <div className={`accordion ${manualAccordionOpen ? 'open' : ''}`} id="manualAccordion">
            <div
              className="accordion-header"
              onClick={() => setManualAccordionOpen((v) => !v)}
            >
              <div className="accordion-left">
                <div className="accordion-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009cff" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </div>
                <div>
                  <div className="accordion-title">Enter HS codes manually</div>
                  <div className="accordion-sub">8-digit codes only — add rows for a small batch before Start Processing</div>
                </div>
              </div>
              <svg className="chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            <div className="accordion-body" id="manualBody">
              <div className="codes-grid-header-simple">
                <div>EU 8-digit code</div>
                <div />
              </div>
              <div id="codeRows">
                {manualLineItems.map((m, idx) => (
                  <div className="code-row-simple" key={idx}>
                    <input
                      className="cell-input mono"
                      type="text"
                      placeholder="00000000"
                      maxLength={10}
                      value={m.eu_cn_8digit}
                      onChange={(e) => {
                        const v = e.target.value;
                        setManualLineItems((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, eu_cn_8digit: v } : x)),
                        );
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        setManualLineItems((prev) => prev.filter((_, i) => i !== idx));
                      }}
                      aria-label="Remove code row"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed text-muted-foreground hover:border-primary hover:text-primary"
                onClick={() => {
                  setManualLineItems((prev) => [...prev, { eu_cn_8digit: '' }]);
                }}
              >
                + Add another code
              </Button>
            </div>
          </div>

          <div className="cta-bar">
            <div>
              <div className="cta-label">Ready to process?</div>
              <div className="cta-sub" id="ctaSub">
                {previewRows.length
                  ? `✓ Ready: ${previewRows.length} line item${previewRows.length === 1 ? '' : 's'} detected`
                  : 'Upload an invoice or enter codes above, then click Start'}
              </div>
            </div>
            <Button
              type="button"
              variant="default"
              size="lg"
              className="shadow-lg shadow-primary/30 hover:shadow-primary/40"
              onClick={() => void startProcessing()}
              disabled={!previewRows.length || conversionLoading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10" fill="none" stroke="white" strokeWidth="2" />
                <polygon points="10 8 16 12 10 16" />
              </svg>
              Start Processing
            </Button>
          </div>

          <div className="privacy-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Data processed securely in this browser session only. No invoice data is stored after you close this tab.
          </div>

          {uploadNotice && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>{uploadNotice}</div>
          )}
          {conversionError && (
            <div style={{ marginTop: 16, fontSize: 12, color: '#ef4444', textAlign: 'center' }}>{conversionError}</div>
          )}
        </div>

        {/* Step 2: Processing */}
        <div className={`step-panel ${currentStep === 2 ? 'active' : ''}`} id="p2">
          <div className="processing-card">
            <div className="spinner-ring" />
            <div className="proc-title">Processing your invoice…</div>
            <div className="proc-sub">
              {uploadedFileName ? `Extracting line items from ${uploadedFileName}` : 'Extracting line items…'}
            </div>

            <div className="proc-steps">
              {[
                { cls: 'pi1', label: 'Invoice uploaded & validated', done: true, step: 0 },
                { cls: 'pi2', label: 'Extracting line items via OCR…', done: processingRunningStep ? processingRunningStep > 1 : false, running: processingRunningStep === 1, step: 1 },
                { cls: 'pi3', label: 'Looking up UK Trade Tariff codes (live HMRC API)', done: processingRunningStep ? processingRunningStep > 2 : false, running: processingRunningStep === 2, step: 2 },
                { cls: 'pi4', label: 'Calculating duty & VAT per line item', done: processingRunningStep ? processingRunningStep > 3 : false, running: processingRunningStep === 3, step: 3 },
                { cls: 'pi5', label: 'Scoring confidence & flagging items for review', done: processingRunningStep ? processingRunningStep > 4 : false, running: processingRunningStep === 4, step: 4 },
              ].map((it) => {
                const isDone = it.done || it.step < (processingRunningStep ?? 0);
                const isRunning = it.running;
                return (
                  <div key={it.cls} className={`proc-item ${isDone ? 'done' : ''} ${isRunning ? 'running' : ''}`}>
                    <div className="proc-dot">{isDone ? '✓' : ''}</div>
                    <span>{it.label}</span>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 32 }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                onClick={resetAll}
                style={{ fontSize: 12, padding: '7px 16px', color: '#4b5563', borderColor: '#1f2937' }}
              >
                ✕ &nbsp;Cancel &nbsp; start again
              </Button>
            </div>
          </div>
        </div>

        {/* Step 3: Review */}
        <div className={`step-panel ${currentStep === 3 ? 'active' : ''}`} id="p3">
          {needsAttentionCount > 0 && (
            <div className="alert-banner">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>
                <strong>{needsAttentionCount} items need your attention</strong> — not auto-confirmed (Ready) or confidence is outside the high band. Review these before exporting to ensure HMRC compliance.
              </span>
            </div>
          )}

          <div className="ca-kpi-row">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className={`ca-kpi-card ca-kpi-card--cyan h-auto flex items-start justify-start ${pillTab === 'all' ? 'ca-kpi-card--active' : ''}`}
              onClick={() => goToExtractedLineItemsTab('all')}
              aria-pressed={pillTab === 'all'}
              aria-label="Show all line items in Extracted Line Items"
            >
              <span className="ca-kpi-card-glow ca-kpi-card-glow--cyan" aria-hidden />
              <span className="ca-kpi-card-inner">
                <span className="ca-kpi-card-label-row">
                  <span className="ca-kpi-card-dot ca-kpi-card-dot--cyan" aria-hidden />
                  <span className="ca-kpi-card-label">Line Items Found</span>
                </span>
                <span className="ca-kpi-card-value">{previewRows.length}</span>
                <span className="ca-kpi-card-meta">From supplied input</span>
              </span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className={`ca-kpi-card ca-kpi-card--green h-auto flex items-start justify-start ${pillTab === 'converted' ? 'ca-kpi-card--active' : ''}`}
              onClick={() => goToExtractedLineItemsTab('converted')}
              aria-pressed={pillTab === 'converted'}
              aria-label="Show converted line items in Extracted Line Items"
            >
              <span className="ca-kpi-card-glow ca-kpi-card-glow--green" aria-hidden />
              <span className="ca-kpi-card-inner">
                <span className="ca-kpi-card-label-row">
                  <span className="ca-kpi-card-dot ca-kpi-card-dot--green" aria-hidden />
                  <span className="ca-kpi-card-label">Codes Converted</span>
                </span>
                <span className="ca-kpi-card-value">{convertedCount}</span>
                <span className="ca-kpi-card-meta">8-digit EU → 10-digit UK</span>
              </span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className={`ca-kpi-card ca-kpi-card--amber h-auto flex items-start justify-start ${pillTab === 'review' ? 'ca-kpi-card--active' : ''}`}
              onClick={() => goToExtractedLineItemsTab('review')}
              aria-pressed={pillTab === 'review'}
              aria-label="Show items needing review in Extracted Line Items"
            >
              <span className="ca-kpi-card-glow ca-kpi-card-glow--amber" aria-hidden />
              <span className="ca-kpi-card-inner">
                <span className="ca-kpi-card-label-row">
                  <span className="ca-kpi-card-dot ca-kpi-card-dot--amber" aria-hidden />
                  <span className="ca-kpi-card-label">Need Attention</span>
                </span>
                <span className="ca-kpi-card-value">{needsAttentionCount}</span>
                <span className="ca-kpi-card-meta">Human review suggested</span>
              </span>
            </Button>
          </div>

          <div className="results-card" ref={extractedLineItemsSectionRef} id="ca-extracted-line-items">
            <div className="results-topbar">
              <div className="results-topbar-left">
                <div className="results-topbar-title">Extracted Line Items</div>
                <div className="ca-data-source-line results-data-source">{UK_TRADE_TARIFF_SOURCE_LINE}</div>
              </div>
              <div className="pill-tabs">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`pill h-auto ${pillTab === 'all' ? 'on' : ''}`}
                  onClick={() => setPillTab('all')}
                >
                  All ({previewRows.length})
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`pill h-auto ${pillTab === 'converted' ? 'on' : ''}`}
                  onClick={() => setPillTab('converted')}
                >
                  Converted ({convertedCount})
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={`pill warn h-auto ${pillTab === 'review' ? 'on' : ''}`}
                  onClick={() => setPillTab('review')}
                >
                  Review ({needsAttentionCount})
                </Button>
              </div>
            </div>

            <ReviewTable
              rowsToRender={pagedReviewRows}
              dutyByCode10={dutyByCode10}
              focusedRowIndex={reviewTableFocusRowIndex}
              onOpenUkOptions={openReviewOptionsModalForRow}
            />
            {filteredReviewRows.length > 0 && (
              <div className="ca-pagination">
                <div className="ca-pagination-left">
                  {(() => {
                    const start = (reviewTablePage - 1) * reviewTableRowsPerPage + 1;
                    const end = Math.min(reviewTablePage * reviewTableRowsPerPage, filteredReviewRows.length);
                    return (
                      <>
                        <span>
                          Showing {start}-{end} of {filteredReviewRows.length} results
                        </span>
                        <div className="ca-pagination-size">
                          <span>Show</span>
                          <select
                            value={reviewTableRowsPerPage}
                            onChange={(e) => {
                              setReviewTableRowsPerPage(Number(e.target.value));
                              setReviewTablePage(1);
                            }}
                          >
                            {[10, 25, 50, 100].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                          <span>per page</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="ca-pagination-right">
                  <span className="ca-pagination-page-label">
                    Page {reviewTablePage}/{reviewTableTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={reviewTablePage === 1}
                    onClick={() => setReviewTablePage((p) => Math.max(1, p - 1))}
                  >
                    &lt;
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={reviewTableTotalPages}
                    value={reviewTablePage}
                    onChange={(e) => {
                      let val = Number(e.target.value);
                      if (!Number.isFinite(val)) val = 1;
                      if (val < 1) val = 1;
                      if (val > reviewTableTotalPages) val = reviewTableTotalPages;
                      setReviewTablePage(val);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={reviewTablePage === reviewTableTotalPages}
                    onClick={() => setReviewTablePage((p) => Math.min(reviewTableTotalPages, p + 1))}
                  >
                    &gt;
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="action-bar" id="actionBarNormal" style={exportConfirmOpen ? { display: 'none' } : undefined}>
            <div>
              <div className="action-bar-title">Confirm &amp; export</div>
              <div className="action-bar-sub">
                {convertedCount} items confirmed · {needsAttentionCount} awaiting review · Invoice total: €{previewRows.reduce((s, r) => s + (Number(r.net_eur) || 0), 0).toFixed(2)}
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="h-auto bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
                onClick={() => {
                  goStep(1);
                  scrollToUploadSectionSmooth();
                }}
              >
                ← Back to upload
              </Button>
              <Button type="button" variant="default" size="md" className="h-auto shadow-lg shadow-primary/25 hover:shadow-primary/35" onClick={() => setExportConfirmOpen(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export to CSV
              </Button>
              <Button
                type="button"
                variant="warning"
                size="md"
                className="h-auto shadow-lg shadow-warning/30"
                onClick={() => {
                  /* Export to TMS — wire when TMS integration is available */
                }}
              >
                Export to TMS
              </Button>
            </div>
          </div>

          <div
            className="action-bar"
            id="actionBarConfirm"
            style={
              exportConfirmOpen
                ? { display: 'flex', background: 'rgba(0,156,255,0.06)', borderColor: 'rgba(0,156,255,0.3)' }
                : { display: 'none' }
            }
          >
            <div>
              <div className="action-bar-title" style={{ color: '#009cff' }}>
                Ready to generate your CSV file?
              </div>
              <div className="action-bar-sub">
                This will create a CSV for import · {needsAttentionCount} flagged item{needsAttentionCount === 1 ? '' : 's'} will be included with notes for human review
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Button type="button" variant="outline" size="md" className="h-auto bg-card text-muted-foreground hover:bg-secondary hover:text-foreground" onClick={() => setExportConfirmOpen(false)}>
                ← Not yet
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                className="h-auto shadow-lg shadow-primary/25 hover:shadow-primary/35"
                onClick={() => {
                  generateExportCsvText();
                  goStep(4);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Yes — generate file
              </Button>
            </div>
          </div>
        </div>

        {/* Step 4: Done */}
        <div className={`step-panel ${currentStep === 4 ? 'active' : ''}`} id="p4">
          <div className="done-card">
            <div className="done-tick">✓</div>
            <div className="done-title">Customs entry complete</div>
            <div className="done-sub">Your CSV file is generated and ready to download</div>
            <div className="done-stats">
              <div className="ds">
                <div className="ds-val">{previewRows.length}</div>
                <div className="ds-lbl">Items processed</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Button type="button" variant="outline" size="md" className="h-auto bg-card text-muted-foreground hover:bg-secondary hover:text-foreground" onClick={() => goStep(3)}>
                ← Review items
              </Button>
              <Button type="button" variant="default" size="md" className="h-auto shadow-lg shadow-primary/25 hover:shadow-primary/35" onClick={downloadOutputCsv}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download CSV
              </Button>
              <Button type="button" variant="outline" size="md" className="h-auto bg-card text-muted-foreground hover:bg-secondary hover:text-foreground" onClick={resetAll}>
                Process another invoice
              </Button>
            </div>
          </div>
        </div>
      </div>

      <KPIDataModal
        isOpen={modal.open}
        onClose={() =>
          setModal((m) => ({
            ...m,
            open: false,
            ukOptionsHs8: undefined,
            ukOptionsInvoiceLine: undefined,
          }))
        }
        title={modal.title}
        subtitle={modal.subtitle}
        icon={FileText}
        data={modal.data as UkOption[]}
        columns={kpiModalColumns}
        searchKeys={kpiModalColumns.map((c) => c.key)}
        exportFilename={`customs_agent_${new Date().toISOString().split('T')[0]}.csv`}
        footerNote={modal.footerNote}
        isLoading={modal.isLoading}
        emptyStateMessage={modal.emptyStateMessage}
        footerActions={ukOptionsModalFooter}
      />
    </div>
  );
}

